import React, { useState, useEffect, useRef, useMemo } from "react";
import { C, fmt, thisMonthStr, getDaysInMonth, getMonthLabel, TX_KEY, DEBT_KEY, RECUR_KEY, GOALS_KEY, ZAKAT_KEY, NW_KEY, SETTINGS_KEY, BUDGETS_KEY, RECUR_STATUS_KEY, load, save, halalBucket, getCat, getDebt, setCurrencySymbol, FontLink, CURRENCIES } from "./constants";
import { callAI, Card, Label, Tag } from "./ui";
import { SpendingChart, CalendarView, WeeklyChart, DonutChart } from "./charts";
import { QuickAdd } from "./QuickAdd";
import { Sprout, Home, CalendarDays, ChevronLeft, ChevronRight, Trash2, Settings } from "lucide-react";
import { RecurringTab, ZakatTab, GoalsTab, DebtTab, BudgetEditTab } from "./tabs";
import { BarakahHabitsTab } from "./BarakahHabitsTab";
// import { AiChat } from "./AiChat"; // hidden until native app feedback confirms demand
import { buildDemoData } from "./demoData";

import { Onboarding } from "./Onboarding";
import { PrivacyPage, TermsPage, SupportPage } from "./Legal";
import { LandingPage } from "./Landing";
import { TestRunner } from "./TestRunner";
import { Tour } from "./Tour";
import { ProBadge, UpgradeModal, useUpgradeNudge, verifyProToken, handleStripeSuccess, isPro } from "./Pro";
import { trackOnce, track, trackReviewShown, copyAnalyticsToClipboard } from "./analytics";
import { logInfo, logWarn } from "./logger";

// Hijri month label — approximate conversion for display
const HIJRI_MONTHS = ["Muharram","Safar","Rabi al-Awwal","Rabi al-Thani","Jumada al-Awwal","Jumada al-Thani","Rajab","Sha'ban","Ramadan","Shawwal","Dhul Qi'dah","Dhul Hijjah"];
function getHijriMonthLabel(ym) {
  const [y, m] = ym.split("-").map(Number);
  const now = new Date();
  const day = (y === now.getFullYear() && m === now.getMonth() + 1) ? now.getDate() : 15;
  const a = Math.floor((14 - m) / 12), yr = y + 4800 - a, mo = m + 12 * a - 3;
  const jdn = day + Math.floor((153*mo+2)/5) + 365*yr + Math.floor(yr/4) - Math.floor(yr/100) + Math.floor(yr/400) - 32045;
  const l = jdn - 1948440 + 10632, n = Math.floor((l-1)/10631), l2 = l - 10631*n + 354;
  const j = Math.floor((10985-l2)/5316)*Math.floor((50*l2)/17719) + Math.floor(l2/5670)*Math.floor((43*l2)/15238);
  const l3 = l2 - Math.floor((30-j)/15)*Math.floor((17719*j)/50) - Math.floor(j/16)*Math.floor((15238*j)/43) + 29;
  const hm = Math.floor((24*l3)/709), hy = 30*n + j - 30;
  return `${HIJRI_MONTHS[(hm-1)%12]} ${hy} AH`;
}

// Debounced save — avoids JSON.stringify on every keystroke
function useDebouncedSave(key, value, delay = 600) {
  useEffect(() => {
    const t = setTimeout(() => save(key, value), delay);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);
}


// ── SETTINGS DRAWER ───────────────────────────────────────────
// ── FEEDBACK SECTION ────────────────────────────────────────
function FeedbackSection({ copyAnalytics }) {
  const [open, setOpen]         = useState(false);
  const [useful, setUseful]     = useState(null);
  const [confused, setConfused] = useState("");
  const [comeback, setComeback] = useState("");
  const [saved, setSaved]       = useState(false);
  const [copied, setCopied]     = useState(false);
  const contentRef              = useRef(null);

  // Detect reduced-motion preference once
  const reducedMotion = typeof window !== "undefined" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  // Animate height on open/close
  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    if (reducedMotion) {
      el.style.height   = open ? "auto" : "0px";
      el.style.opacity  = open ? "1" : "0";
      return;
    }
    if (open) {
      // Expand: measure scrollHeight then animate to it
      el.style.height  = "0px";
      el.style.opacity = "0";
      el.style.display = "block";
      requestAnimationFrame(() => {
        el.style.transition = "height 200ms ease-out, opacity 200ms ease-out";
        el.style.height     = el.scrollHeight + "px";
        el.style.opacity    = "1";
        const done = () => { el.style.height = "auto"; el.removeEventListener("transitionend", done); };
        el.addEventListener("transitionend", done);
      });
    } else {
      // Collapse: freeze height then animate to 0
      el.style.height     = el.scrollHeight + "px";
      el.style.transition = "height 150ms ease-in, opacity 120ms ease-in";
      requestAnimationFrame(() => {
        el.style.height  = "0px";
        el.style.opacity = "0";
      });
    }
  }, [open, reducedMotion]);

  const handleSave = () => {
    if (useful === null && !confused && !comeback) return;
    const entry = { ts: Date.now(), useful, confused: confused.trim()||null, comeback: comeback.trim()||null };
    try {
      const existing = JSON.parse(localStorage.getItem("bb_feedback") || "[]");
      existing.push(entry);
      // Cap at 50 entries — prevents unbounded localStorage growth
      if (existing.length > 50) existing.splice(0, existing.length - 50);
      localStorage.setItem("bb_feedback", JSON.stringify(existing));
      track("feedback_submitted");
    } catch { return; }
    setSaved(true);
    setUseful(null); setConfused(""); setComeback("");
    setTimeout(() => { setSaved(false); setOpen(false); }, 2500);
  };

  const handleCopy = async () => {
    const ok = await copyAnalytics();
    setCopied(ok);
    setTimeout(() => setCopied(false), 3000);
  };

  const inp = { width:"100%", background:C.surface, border:`1px solid ${C.border2}`,
    borderRadius:8, padding:"10px 12px", color:C.text, fontSize:12,
    fontFamily:"'DM Sans',sans-serif", outline:"none", resize:"none", boxSizing:"border-box" };

  return (
    <div style={{ marginBottom:16 }}>
      <div style={{ fontSize:11, fontWeight:700, color:C.text3, letterSpacing:"0.08em",
        padding:"16px 0 8px", textTransform:"uppercase" }}>Feedback</div>

      {/* Accordion trigger */}
      <button
        aria-expanded={open}
        aria-controls="feedback-form"
        onClick={() => setOpen(o => !o)}
        style={{ width:"100%", background:C.surface2,
          border:`1px solid ${C.border}`,
          borderRadius: open ? "14px 14px 0 0" : 14,
          padding:"14px 16px", cursor:"pointer",
          display:"flex", justifyContent:"space-between", alignItems:"center",
          transition:"border-radius 200ms ease-out" }}>
        <span style={{ fontSize:13, color:C.text, fontWeight:600 }}>Send feedback</span>
        {/* Chevron — rotates 180° when open */}
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
          stroke={C.text3} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
          style={{ transition: reducedMotion ? "none" : "transform 200ms ease-out",
            transform: open ? "rotate(180deg)" : "rotate(0deg)", flexShrink:0 }}>
          <path d="M6 9l6 6 6-6"/>
        </svg>
      </button>

      {/* Accordion body — height + opacity animated via useEffect */}
      <div
        id="feedback-form"
        ref={contentRef}
        style={{ overflow:"hidden", height:"0px", opacity:0,
          background:C.surface2, border:`1px solid ${C.border}`,
          borderTop:"none", borderRadius:"0 0 14px 14px" }}>
        <div style={{ padding:"16px" }}>
          {saved ? (
            <div style={{ textAlign:"center", padding:"16px 0",
              color:C.green, fontWeight:700, fontSize:14, animation:"fadeIn 0.2s ease" }}>
              ✓ JazakAllah khair — feedback saved.
            </div>
          ) : (
            <>
              <div style={{ fontSize:13, color:C.text, fontWeight:600, marginBottom:10 }}>
                Was this useful?
              </div>
              <div style={{ display:"flex", gap:8, marginBottom:16 }}>
                {[{v:true,l:"👍 Yes"},{v:false,l:"👎 No"}].map(({v,l}) => (
                  <button key={String(v)} onClick={() => setUseful(v)}
                    style={{ flex:1, padding:"9px", borderRadius:10, cursor:"pointer",
                      fontSize:12, fontWeight:600,
                      background: useful===v ? (v?"rgba(61,214,140,0.15)":"rgba(240,82,82,0.15)") : C.surface,
                      border: useful===v ? `1px solid ${v?C.green:C.red}` : `1px solid ${C.border}`,
                      color: useful===v ? (v?C.green:C.red) : C.text2 }}>{l}</button>
                ))}
              </div>
              <div style={{ fontSize:13, color:C.text, fontWeight:600, marginBottom:6 }}>
                What confused you?
              </div>
              <textarea rows={2} value={confused} onChange={e=>setConfused(e.target.value)}
                placeholder="Anything unclear or hard to use..." style={inp} />
              <div style={{ fontSize:13, color:C.text, fontWeight:600, margin:"12px 0 6px" }}>
                What would make you come back next Sunday?
              </div>
              <textarea rows={2} value={comeback} onChange={e=>setComeback(e.target.value)}
                placeholder="Be honest — what's missing?" style={inp} />
              <button onClick={handleSave}
                style={{ width:"100%", marginTop:12, padding:"11px", borderRadius:10,
                  background:C.surface, border:`1px solid ${C.border2}`,
                  color:C.text2, fontSize:12, fontWeight:700, cursor:"pointer" }}>
                Submit Feedback
              </button>
            </>
          )}
        </div>
      </div>

      {/* Analytics export */}
      <button onClick={handleCopy}
        style={{ width:"100%", marginTop:8, padding:"11px", borderRadius:10,
          background:"none", border:`1px solid ${C.border}`,
          color: copied?C.green:C.text3, fontSize:11, fontWeight:600, cursor:"pointer" }}>
        {copied ? "✓ Copied to clipboard" : "📋 Copy analytics to clipboard"}
      </button>
      <div style={{ fontSize:10, color:C.text3, textAlign:"center", marginTop:4, lineHeight:1.5 }}>
        Shares only usage events — no transactions, no amounts, no personal data.
      </div>
    </div>
  );
}


function SettingsTab({ settings, setSettings, budgetData, setBudgetData, proUser, openUpgrade,
  transactions, debtState, recurring, goals, zakatData, netWorthData, onImport, doExport }) {

  const [name, setName]           = useState(settings?.name || "");
  const [currency, setCurrency]   = useState(settings?.currency || "CAD");
  const [section, setSection]     = useState("main"); // main | budget | data
  const [importing, setImporting] = useState(false);
  const [adminVisible, setAdminVisible] = useState(false);
  const [adminCode, setAdminCode]       = useState("");
  const [editingProfile, setEditingProfile] = useState(!settings?.name); // open if no name yet
  const fileRef = useRef();

  const cur = CURRENCIES.find(c => c.code === currency) || CURRENCIES[1];

  const saveProfile = () => {
    const updated = { ...settings, name: name.trim(), currency, currencySymbol: cur.symbol };
    setSettings(updated);
    setCurrencySymbol(cur.symbol);
    setEditingProfile(false);
  };

 
  const doImport = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImporting(true);
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const data = JSON.parse(ev.target.result);
        if (Array.isArray(data.transactions)) { logInfo("storage", "backup_import_success", { tx_count: data.transactions?.length }); onImport(data); window.dispatchEvent(new CustomEvent("bb-toast", { detail: { msg: "✅ Backup restored!", ok: true } })); }
        else { logWarn("storage", "backup_import_invalid"); window.dispatchEvent(new CustomEvent("bb-toast", { detail: { msg: "❌ Invalid backup file.", ok: false } })); }
      } catch { window.dispatchEvent(new CustomEvent("bb-toast", { detail: { msg: "❌ Could not read file.", ok: false } })); }
      setImporting(false);
      e.target.value = "";
    };
    reader.readAsText(file);
  };

  const row = (label, value, onClick, danger=false, badge=null, last=false) => (
    <button onClick={onClick} style={{
      width:"100%", display:"flex", alignItems:"center", justifyContent:"space-between",
      background:"none", border:"none", padding:"13px 0", cursor:"pointer",
      borderBottom: last ? "none" : `1px solid ${C.border}`,
    }}>
      <span style={{ color: danger ? C.red : C.text, fontSize:14 }}>{label}</span>
      <div style={{ display:"flex", alignItems:"center", gap:8 }}>
        {badge && <span style={{ fontSize:10, color:C.purple, fontWeight:700, background:"rgba(167,139,250,0.12)", borderRadius:4, padding:"2px 6px" }}>{badge}</span>}
        {value && <span style={{ color:C.text3, fontSize:13 }}>{value}</span>}
        <span style={{ color:C.text3, fontSize:14 }}>›</span>
      </div>
    </button>
  );

  return (
    <div className="fade-up">
      {/* Header — only show when in a sub-section (main title is in the app header) */}
      {section !== "main" && (
        <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:20 }}>
          <button onClick={() => setSection("main")}
            style={{ background:C.surface2, border:`1px solid ${C.border2}`,
              color:C.text2, fontSize:13, fontWeight:600, borderRadius:10,
              padding:"8px 14px", cursor:"pointer", display:"flex", alignItems:"center", gap:6 }}>
            ← Back
          </button>
          <span style={{ color:C.text, fontWeight:800, fontSize:18, letterSpacing:"-0.02em" }}>
            {section === "budget" ? "Budget Settings" : "Data & Backup"}
          </span>
        </div>
      )}

      <div>

          {/* ── MAIN SECTION ── */}
          {section === "main" && (
            <>
              {/* Profile — collapsed card or edit form */}
              <div style={{ color:C.text3, fontSize:11, fontWeight:700, letterSpacing:"0.08em", textTransform:"uppercase", marginBottom:8 }}>Account</div>
              <div style={{ background:C.surface2, borderRadius:14, marginBottom:20, overflow:"hidden" }}>
                {!editingProfile ? (
                  /* Collapsed: show name + currency, edit button */
                  <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"14px 16px" }}>
                    <div style={{ display:"flex", alignItems:"center", gap:12 }}>
                      <div style={{ width:38, height:38, borderRadius:12, background:"rgba(61,214,140,0.12)",
                        display:"flex", alignItems:"center", justifyContent:"center", fontSize:18 }}>
                        {(name||settings?.name||"?")[0]?.toUpperCase()}
                      </div>
                      <div>
                        <div style={{ color:C.text, fontWeight:700, fontSize:14 }}>{name || settings?.name || "No name"}</div>
                        <div style={{ color:C.text3, fontSize:11, marginTop:1 }}>{cur.symbol} · {cur.label}</div>
                      </div>
                    </div>
                    <button onClick={() => setEditingProfile(true)}
                      style={{ background:C.surface3, border:`1px solid ${C.border}`, color:C.text2,
                        borderRadius:8, padding:"6px 14px", fontSize:12, fontWeight:600, cursor:"pointer" }}>
                      Edit
                    </button>
                  </div>
                ) : (
                  /* Expanded: full edit form */
                  <div style={{ padding:"16px" }}>
                    <div style={{ color:C.text3, fontSize:11, fontWeight:600, letterSpacing:"0.08em", textTransform:"uppercase", marginBottom:12 }}>Profile</div>
                    <div style={{ marginBottom:10 }}>
                      <div style={{ color:C.text3, fontSize:11, marginBottom:5 }}>Your name</div>
                      <input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Ahmed"
                        autoFocus
                        style={{ width:"100%", background:C.surface, border:`1px solid ${C.border2}`, borderRadius:10,
                          padding:"10px 14px", color:C.text, fontSize:15, fontFamily:"'DM Sans',sans-serif", outline:"none" }} />
                    </div>
                    <div style={{ marginBottom:12 }}>
                      <div style={{ color:C.text3, fontSize:11, marginBottom:5 }}>Currency</div>
                      <select value={currency} onChange={e => setCurrency(e.target.value)}
                        style={{ width:"100%", background:C.surface, border:`1px solid ${C.border2}`, borderRadius:10,
                          padding:"10px 14px", color:C.text, fontSize:14, fontFamily:"'DM Sans',sans-serif", outline:"none" }}>
                        {CURRENCIES.map(c => <option key={c.code} value={c.code}>{c.symbol} — {c.label}</option>)}
                      </select>
                    </div>
                    <div style={{ display:"flex", gap:8 }}>
                      <button onClick={() => setEditingProfile(false)}
                        style={{ flex:1, padding:"10px", borderRadius:10, border:`1px solid ${C.border}`,
                          background:"none", color:C.text2, fontSize:13, fontWeight:600, cursor:"pointer" }}>
                        Cancel
                      </button>
                      <button onClick={saveProfile}
                        style={{ flex:2, padding:"10px", borderRadius:10, border:"none", cursor:"pointer",
                          background:C.green, color:"#0e0e13", fontSize:14, fontWeight:700 }}>
                        Save
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Pro status */}
              <div style={{ color:C.text3, fontSize:11, fontWeight:700, letterSpacing:"0.08em", textTransform:"uppercase", marginBottom:8 }}>Plan</div>
              <div style={{ background:C.surface2, borderRadius:14, padding:"14px 16px", marginBottom:20 }}>
                {proUser ? (
                  <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                    <div>
                      <div style={{ color:C.text, fontWeight:700, fontSize:14 }}>Barakah Pro</div>
                      <div style={{ color:C.green, fontSize:11, marginTop:2 }}>Active</div>
                    </div>
                    <span style={{ fontSize:10, color:C.purple, fontWeight:700, background:"rgba(167,139,250,0.12)", borderRadius:4, padding:"3px 8px" }}>PRO</span>
                  </div>
                ) : (
                  <button onClick={() => openUpgrade("settings_plan")}
                    style={{ width:"100%", display:"flex", alignItems:"center", justifyContent:"space-between",
                      background:"none", border:"none", cursor:"pointer", padding:0 }}>
                    <div>
                      <div style={{ color:C.text, fontWeight:700, fontSize:14, textAlign:"left" }}>Free Plan</div>
                      <div style={{ color:C.text3, fontSize:11, marginTop:2 }}>Go Pro — halal debt payoff strategy, manual budget control. CA$4.99/mo</div>
                    </div>
                    <span style={{ fontSize:12, color:C.purple, fontWeight:700 }}>Upgrade →</span>
                  </button>
                )}
              </div>

              {/* App settings */}
              <div style={{ color:C.text3, fontSize:11, fontWeight:700, letterSpacing:"0.08em", textTransform:"uppercase", marginBottom:8 }}>App</div>
              <div style={{ background:C.surface2, borderRadius:14, padding:"0 16px", marginBottom:20 }}>
                {row("💾 Data & Backup", "", () => setSection("data"))}
                {(() => {
                  const lastBackup = localStorage.getItem("bb_last_backup");
                  const txAtBackup = parseInt(localStorage.getItem("bb_tx_count_at_backup") || "0");
                  const txSince    = transactions.length - txAtBackup;
                  if (!lastBackup) return (
                    <div style={{ fontSize:11, color:C.amber, lineHeight:1.6, padding:"6px 0 10px" }}>
                      ⚠️ No backup saved yet — tap above to export one
                    </div>
                  );
                  const daysAgo = Math.floor((Date.now() - parseInt(lastBackup)) / 86400000);
                  const color   = daysAgo >= 14 ? C.red : daysAgo >= 7 ? C.amber : C.green;
                  const when    = daysAgo === 0 ? "today" : daysAgo === 1 ? "yesterday" : `${daysAgo} days ago`;
                  const since   = txSince > 0 ? ` · ${txSince} new transaction${txSince !== 1 ? "s" : ""}` : " · Up to date";
                  return (
                    <div style={{ fontSize:11, color, lineHeight:1.6, padding:"6px 0 10px" }}>
                      {daysAgo >= 7 ? "⚠️" : "✓"} Last backup: {when}{since}
                    </div>
                  );
                })()}

                {row("⚙️ Budget Settings", "", () => setSection("budget"), false, null, true)}
              </div>

              {/* Feedback + Analytics */}
              <div style={{ color:C.text3, fontSize:11, fontWeight:700, letterSpacing:"0.08em", textTransform:"uppercase", marginBottom:8 }}>Feedback</div>
              <div style={{ marginBottom:20 }}>
                <FeedbackSection copyAnalytics={copyAnalyticsToClipboard} />
              </div>

              {/* Danger zone */}
              <div style={{ color:"rgba(240,82,82,0.7)", fontSize:11, fontWeight:700, letterSpacing:"0.08em", textTransform:"uppercase", marginBottom:8 }}>Danger Zone</div>
              <div style={{ background:"rgba(240,82,82,0.04)", border:"1px solid rgba(240,82,82,0.12)", borderRadius:14, padding:"0 16px", marginBottom:16 }}>
                <button onClick={() => {
                  if (!window.confirm("Delete ALL your data permanently? This cannot be undone.")) return;
                  Object.keys(localStorage).filter(k => k.startsWith("bb_")).forEach(k => localStorage.removeItem(k));
                  window.location.reload();
                }} style={{ width:"100%", display:"flex", alignItems:"center", justifyContent:"space-between",
                  background:"none", border:"none", padding:"15px 0", cursor:"pointer" }}>
                  <span style={{ color:C.red, fontSize:14 }}>🗑️ Reset All Data</span>
                  <span style={{ color:C.text3, fontSize:14 }}>›</span>
                </button>
              </div>

              {/* About */}
              <div style={{ color:C.text3, fontSize:11, fontWeight:700, letterSpacing:"0.08em", textTransform:"uppercase", marginBottom:8 }}>About</div>
              <div style={{ background:C.surface2, borderRadius:14, padding:"14px 16px", marginBottom:20 }}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:10 }}>
                  <span style={{ color:C.text, fontSize:14 }}>Barakah Budget</span>
                  <span style={{ color:C.text3, fontSize:12 }}>v1.0.1</span>
                </div>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:10, paddingTop:10, borderTop:`1px solid ${C.border}` }}>
                  <span style={{ color:C.text, fontSize:14 }}>Website</span>
                  <a href="https://barakahbudget.com" target="_blank" rel="noreferrer"
                    style={{ color:C.green, fontSize:13, textDecoration:"none" }}>barakahbudget.com ↗</a>
                </div>
                <div style={{ paddingTop:10, borderTop:`1px solid ${C.border}` }}>
                  <div style={{ color:C.text3, fontSize:11, lineHeight:1.6 }}>
                    🔒 Your data never leaves your device. No accounts, no servers, no tracking.
                  </div>
                </div>
              </div>

              {/* Admin — hidden */}
              {!adminVisible ? (
                <button onClick={() => setAdminVisible(true)}
                  style={{ width:"100%", background:"none", border:"none", color:C.text3, fontSize:11,
                    padding:"10px 0", cursor:"pointer", textAlign:"center", letterSpacing:"0.12em" }}>
                  ···
                </button>
              ) : (
                <div style={{ background:C.surface2, borderRadius:12, padding:14, marginBottom:16 }}>
                  <input autoFocus type="password" placeholder="Admin code"
                    value={adminCode} onChange={e => setAdminCode(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === "Enter") {
                        if (adminCode === "bb-admin-mufeed-2026") {
                          const isCurrentlyPro = isPro();
                          if (isCurrentlyPro) {
                            localStorage.removeItem("bb_pro_token");
                            localStorage.removeItem("bb_pro_verified_at");
                            localStorage.setItem("bb_pro", "false");
                          } else {
                            // Admin-only local token — not a real Stripe token
                            localStorage.setItem("bb_pro_token", "admin-local-token");
                            localStorage.setItem("bb_pro", "true");
                            localStorage.setItem("bb_pro_verified_at", Date.now().toString());
                          }
                          window.location.reload();
                        } else { setAdminCode(""); setAdminVisible(false); }
                      }
                      if (e.key === "Escape") { setAdminVisible(false); setAdminCode(""); }
                    }}
                    style={{ width:"100%", background:C.surface, border:`1px solid ${C.border2}`,
                      borderRadius:8, padding:"10px 12px", color:C.text, fontSize:13,
                      fontFamily:"'DM Sans',sans-serif", outline:"none" }} />
                  <div style={{ color:C.text3, fontSize:10, marginTop:6 }}>
                    {proUser ? "Currently: PRO — code switches to Free" : "Currently: Free — code switches to Pro"}
                  </div>
                </div>
              )}
            </>
          )}

          {/* ── BUDGET SECTION ── */}
          {section === "budget" && (
            <BudgetEditTab budgetData={budgetData} setBudgetData={setBudgetData} settings={settings} proUser={proUser} openUpgrade={openUpgrade} />
          )}

          {/* ── DATA SECTION ── */}
          {section === "data" && (
            <div>
              <div style={{ color:C.text3, fontSize:11, fontWeight:700, letterSpacing:"0.08em", textTransform:"uppercase", marginBottom:8 }}>Backup</div>
              <div style={{ background:C.surface2, borderRadius:14, padding:"0 16px", marginBottom:16 }}>
                <button onClick={doExport}
                  style={{ width:"100%", display:"flex", alignItems:"center", justifyContent:"space-between",
                    background:"none", border:"none", padding:"16px 0", cursor:"pointer", borderBottom:`1px solid ${C.border}` }}>
                  <div style={{ textAlign:"left" }}>
                    <div style={{ color:C.text, fontSize:14, fontWeight:600 }}>📤 Export Backup</div>
                    <div style={{ color:C.text3, fontSize:11, marginTop:2 }}>Download your full data as JSON</div>
                  </div>
                </button>
                <button onClick={() => {  fileRef.current?.click(); }}
                  style={{ width:"100%", display:"flex", alignItems:"center", justifyContent:"space-between",
                    background:"none", border:"none", padding:"16px 0", cursor:"pointer" }}>
                  <div style={{ textAlign:"left" }}>
                    <div style={{ color:C.text, fontSize:14, fontWeight:600 }}>📥 {importing ? "Importing…" : "Import Backup"}</div>
                    <div style={{ color:C.text3, fontSize:11, marginTop:2 }}>Restore from a backup file</div>
                  </div>
                </button>
                <input ref={fileRef} type="file" accept=".json" onChange={doImport} style={{ display:"none" }} />
              </div>
              {(() => {
                const lastBackup = localStorage.getItem("bb_last_backup");
                const txAtBackup = parseInt(localStorage.getItem("bb_tx_count_at_backup") || "0");
                const txSince = transactions.length - txAtBackup;
                if (!lastBackup) return (
                  <div style={{ background:"rgba(240,82,82,0.07)", border:"1px solid rgba(240,82,82,0.2)",
                    borderRadius:12, padding:"12px 14px", marginBottom:12 }}>
                    <div style={{ color:C.red, fontSize:12, fontWeight:700 }}>⚠️ No backup yet</div>
                    <div style={{ color:C.text3, fontSize:11, marginTop:4, lineHeight:1.6 }}>
                      You have {transactions.length} transaction{transactions.length !== 1 ? "s" : ""} with no backup. Export one above.
                    </div>
                  </div>
                );
                const daysAgo = Math.floor((Date.now() - parseInt(lastBackup)) / 86400000);
                const statusColor = daysAgo >= 14 ? C.red : daysAgo >= 7 ? C.amber : C.green;
                const statusBg    = daysAgo >= 14 ? "rgba(240,82,82,0.07)"   : daysAgo >= 7 ? "rgba(245,166,35,0.07)"  : "rgba(61,214,140,0.07)";
                const statusBdr   = daysAgo >= 14 ? "rgba(240,82,82,0.25)"   : daysAgo >= 7 ? "rgba(245,166,35,0.25)"  : "rgba(61,214,140,0.2)";
                const statusIcon  = daysAgo >= 7 ? "⚠️" : "✓";
                return (
                  <div style={{ background:statusBg, border:`1px solid ${statusBdr}`,
                    borderRadius:12, padding:"12px 14px", marginBottom:12 }}>
                    <div style={{ color:statusColor, fontSize:12, fontWeight:700 }}>
                      {statusIcon} Last backup: {daysAgo === 0 ? "today" : daysAgo === 1 ? "yesterday" : `${daysAgo} days ago`}
                    </div>
                    <div style={{ color:C.text3, fontSize:11, marginTop:4 }}>
                      {txSince > 0 ? `${txSince} new transaction${txSince !== 1 ? "s" : ""} since last backup` : "Up to date"}
                    </div>
                  </div>
                );
              })()}
              <div style={{ background:"rgba(245,166,35,0.07)", border:"1px solid rgba(245,166,35,0.2)",
                borderRadius:12, padding:"12px 14px", marginBottom:12 }}>
                <div style={{ color:C.amber, fontSize:12, fontWeight:700, marginBottom:4 }}>⚠️ Safari data warning</div>
                <div style={{ color:C.text3, fontSize:11, lineHeight:1.7 }}>
                  Your data is stored on this device only. Safari may clear it automatically if storage runs low.
                  Export a backup regularly so you never lose your data.
                </div>
              </div>
              <div style={{ color:C.text3, fontSize:11, textAlign:"center", padding:"0 0 8px", lineHeight:1.7 }}>
                No servers. No accounts. No ads.
              </div>
            </div>
          )}

        </div>
    </div>
  );
}

export default function App() {
  // State
  const [transactions, setTransactions] = useState(() => load(TX_KEY, []));
  const [debtState,    setDebtState]    = useState(() => load(DEBT_KEY, {debts:[],payments:[]}));
  const [recurring,    setRecurring]    = useState(() => load(RECUR_KEY, []));
  const [goals,        setGoals]        = useState(() => load(GOALS_KEY, []));
  const [zakatData,    setZakatData]    = useState(() => load(ZAKAT_KEY, {}));
  const [netWorthData, setNetWorthData] = useState(() => load(NW_KEY, { adjustments:[] }));
  const [recurringStatus, setRecurringStatus] = useState(() => load(RECUR_STATUS_KEY, {}));
  const [settings, setSettings] = useState(() => load(SETTINGS_KEY, null));
  const [budgetData, setBudgetData] = useState(() => load(BUDGETS_KEY, null));
  const [onboarded, setOnboarded] = useState(() => !!load(SETTINGS_KEY, null));

  // UI state
  const [month,      setMonth]      = useState(thisMonthStr());
  const [tab,        setTab]        = useState("overview");
  const [toolsTab,   setToolsTab]   = useState(null);
  const [barakahEnabled, setBarakahEnabled] = useState(() => load("bb_barakah_enabled", null) === "true");
  const [selDay,     setSelDay]     = useState(null);
  const [showAllTxns,   setShowAllTxns]   = useState(false);

  const [undoToast,     setUndoToast]     = useState(null); // {label, action}
  const [showTests,   setShowTests]   = useState(false);
  const [showTour,    setShowTour]    = useState(false);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [upgradeTrigger, setUpgradeTrigger] = useState("general");
  const openUpgrade = (trigger = "general") => { setUpgradeTrigger(trigger); setShowUpgrade(true); };
  const { checkNudges, NudgeBanner, ModalIfOpen } = useUpgradeNudge();
  const tapCount = useRef(0);
  const tapTimer = useRef(null);
  const [isMobile, setIsMobile] = React.useState(() => window.innerWidth < 640);
  React.useEffect(() => {
    const fn = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', fn);
    return () => window.removeEventListener('resize', fn);
  }, []);
  // const [showChat, setShowChat] = useState(false); // hidden until native app feedback confirms demand
  const [showAdd,    setShowAdd]    = useState(false);
  const [showAddDrawer, setShowAddDrawer] = useState(false);
  // const [suggestions,setSuggestions]= useState([]); // replaced by rule-based moneyInsights
  // const [suggLoad,   setSuggLoad]   = useState(false);
  const [demoLoaded, setDemoLoaded] = useState(() => !!load("bb_demo_loaded", false));
  const [proUser, setProUser]     = useState(() => isPro()); // requires both bb_pro + bb_pro_token
  const [recoveryCardDismissed, setRecoveryCardDismissed] = useState(false);
  const [nudgeDismissedAt, setNudgeDismissedAt] = useState(() => localStorage.getItem("bb_backup_nudge_dismissed") || null);

  const [nwOpen, setNwOpen] = useState(false);
  const [weekReviewed, setWeekReviewed] = useState(false);
  const [proToast, setProToast]         = useState(null); // { msg, ok }

  // Ask browser to keep localStorage persistent — reduces Safari wiping data on storage pressure
  useEffect(() => {
    if (navigator.storage?.persist) navigator.storage.persist();
  }, []);

  // Global toast listener — used by settings drawer which has no direct state access
  useEffect(() => {
    const handler = (e) => { setProToast(e.detail); setTimeout(() => setProToast(null), 4000); };
    window.addEventListener("bb-toast", handler);
    return () => window.removeEventListener("bb-toast", handler);
  }, []);

  // Keep proUser in sync if another tab or the admin code changes it
  useEffect(() => {
    const handler = (e) => {
      if (e.key === "bb_pro" || e.key === "bb_pro_token") setProUser(isPro());
    };
    window.addEventListener("storage", handler);
    return () => window.removeEventListener("storage", handler);
  }, []);

  // Handle Stripe payment success redirect + verify token on load
  useEffect(() => {
    const params    = new URLSearchParams(window.location.search);
    const success   = params.get("success");
    const sessionId = params.get("session_id");

    if (success === "1" && sessionId) {
      // Clear URL params
      window.history.replaceState({}, "", window.location.pathname);
      handleStripeSuccess(sessionId).then(ok => {
        logInfo("auth", ok ? "stripe_verify_success" : "stripe_verify_failed");
        if (ok) {
          setProUser(true);
          setProToast({ msg:"🎉 Welcome to Barakah Pro! JazakAllah khair.", ok:true });
        } else {
          setProToast({ msg:"Payment received but verification failed. Please contact support.", ok:false });
        }
        setTimeout(() => setProToast(null), 5000);
      });
    } else {
      // Verify existing token on every load — always update state from result
      verifyProToken().then(valid => {
        setProUser(valid);
        logInfo("pro_gating", valid ? "token_valid" : "token_invalid_or_missing");
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-load demo if triggered from the reset menu
  useEffect(() => {
    if (localStorage.getItem("bb_load_demo_on_start") === "1") {
      localStorage.removeItem("bb_load_demo_on_start");
      loadDemo();
    }
  }, []);

  const loadDemo = () => {
    const { txns, debts, payments, recurring: rec, goals: g, zakatData: z, budgetData: bd } = buildDemoData();
    // Pre-mark all past months as confirmed so banner only shows for current month
    const pastMonths = [
      "2025-01","2025-02","2025-03","2025-04","2025-05","2025-06",
      "2025-07","2025-08","2025-09","2025-10","2025-11","2025-12","2026-01","2026-02"
    ];
    const demoStatus = {};
    pastMonths.forEach(m => rec.forEach(r => { demoStatus[`${r.id}_${m}`] = 'confirmed'; }));
    save(TX_KEY,    txns);
    save(DEBT_KEY,  { debts, payments });
    save(RECUR_KEY, rec);
    save(GOALS_KEY, g);
    save(ZAKAT_KEY, z);
    save(RECUR_STATUS_KEY, demoStatus);
    save(BUDGETS_KEY, bd);
    save("bb_demo_loaded", true);
    setTransactions(txns);
    setDebtState({ debts, payments });
    setRecurring(rec);
    setGoals(g);
    setZakatData(z);
    setBudgetData(bd);
    setRecurringStatus(demoStatus);
    setDemoLoaded(true);
    setMonth("2026-03");
    // setSuggestions([]);
    setTimeout(() => setShowTour(true), 600);
  };

  const handleImport = (data) => {
    logInfo("storage", "backup_import_started", { txCount: data?.transactions?.length || 0 });
    const txs   = data.transactions  || [];
    const debts = data.debtState     || { debts: [], payments: [] };
    const recur = data.recurring     || [];
    const goals = data.goals         || [];
    const zakat = data.zakatData     || {};
    const nw    = data.netWorthData  || { adjustments: [] };
    save(TX_KEY,    txs);
    save(DEBT_KEY,  debts);
    save(RECUR_KEY, recur);
    save(GOALS_KEY, goals);
    save(ZAKAT_KEY, zakat);
    save(NW_KEY,    nw);
    setTransactions(txs);
    setDebtState(debts);
    setRecurring(recur);
    setGoals(goals);
    setZakatData(zakat);
    setNetWorthData(nw);
  };

  const doExport = () => {
    const data = { version:1, exportedAt: new Date().toISOString(), transactions, debtState, recurring, goals, zakatData, netWorthData };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type:"application/json" });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement("a");
    a.href = url; a.download = `barakah-budget-backup-${new Date().toISOString().slice(0,10)}.json`; a.click();
    URL.revokeObjectURL(url);
    localStorage.setItem("bb_last_backup", Date.now());
    localStorage.setItem("bb_tx_count_at_backup", transactions.length);
  };

  const clearDemo = () => {
    [TX_KEY,DEBT_KEY,RECUR_KEY,GOALS_KEY,ZAKAT_KEY,NW_KEY,"bb_demo_loaded"].forEach(k=>localStorage.removeItem(k));
    setTransactions([]); setDebtState({debts:[],payments:[]}); setRecurring([]);
    setGoals([]); setZakatData({}); setNetWorthData({adjustments:[]}); setDemoLoaded(false);
  };

  // Persist
  // Debounced saves — avoids thrashing localStorage on every keystroke
  useDebouncedSave(TX_KEY,       transactions);
  useDebouncedSave(DEBT_KEY,     debtState);
  useDebouncedSave(RECUR_KEY,    recurring);
  useDebouncedSave(GOALS_KEY,    goals);
  useDebouncedSave(ZAKAT_KEY,    zakatData);
  useDebouncedSave(NW_KEY,       netWorthData);

  // Nudge checks (no debounce needed)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { checkNudges({ txCount: transactions.length, goalCount: goals.length }); }, [transactions.length, goals.length]);





  // Settings — save immediately + update currency symbol
  useEffect(() => {
    if (settings) {
      save(SETTINGS_KEY, settings);
      setCurrencySymbol(settings.currencySymbol || "CA$");
    }
  }, [settings]);

  const creditCards = debtState.debts.filter(d => d.type==="credit" && d.currentBalance>0);
  // appNetSavings = all-time (income − expenses) + manual adjustments
  // This correctly captures BOTH named goal savings AND unallocated monthly surplus.
  // Goals are already embedded in the tx history (e.g. "Hajj savings transfer" is an expense tx
  // that moves money out of spending — the net reflects what you actually kept).
  // Adjustments subtract money no longer in your possession (lent, gifted, withdrawn).
  const goalsTotal   = useMemo(() => goals.reduce((s,g) => s + (g.saved||0), 0), [goals]);
  // Exclude isInterestIncome from net wealth — riba income is not halal wealth
  const allTimeTxNet = useMemo(() => transactions.filter(t => !t.isInterestIncome).reduce((s,t) => s + t.amount, 0), [transactions]);
  const nwAdjTotal   = useMemo(() => (netWorthData.adjustments||[]).reduce((s,a) => s + (a.type==="+" ? a.amount : -a.amount), 0), [netWorthData.adjustments]);
  const unallocated  = Math.max(0, allTimeTxNet - goalsTotal);
  const appNetSavings = Math.max(0, goalsTotal + unallocated + nwAdjTotal);



  // Month transactions — memoized so they don't recalculate on every render
  const monthTxns = useMemo(
    () => transactions.filter(t => t.date.startsWith(month)),
    [transactions, month]
  );
  // Exclude isInterestIncome from halal income calculations
  const monthIncome   = useMemo(() => monthTxns.filter(t=>t.amount>0 && !t.isInterestIncome).reduce((s,t)=>s+t.amount,0), [monthTxns]);
  const monthExpenses = useMemo(() => monthTxns.filter(t=>t.amount<0).reduce((s,t)=>s+Math.abs(t.amount),0), [monthTxns]);
  const monthSaved    = monthIncome - monthExpenses;

  // Weekly calc — always last 7 days regardless of selected month
  const weekStart    = (() => { const d = new Date(); d.setDate(d.getDate()-6); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`; })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const weekTxns     = useMemo(() => transactions.filter(t => t.date >= weekStart), [transactions]); // weekStart stable per session
  const weekExpenses = useMemo(() => weekTxns.filter(t=>t.amount<0).reduce((s,t)=>s+Math.abs(t.amount),0), [weekTxns]);
  // Show weekly if viewing current month, monthly if viewing a past month
  const isCurrentMonth = month === thisMonthStr();
  const showWeekly   = isCurrentMonth;

  const addTx = (txn) => {
    if (transactions.length === 0) trackOnce("first_expense_added");
    logInfo("transactions", "add_tx", { category: txn.expenseCategory, type: txn.amount > 0 ? "income" : "expense" });
    logInfo("transactions", txn.amount < 0 ? "expense_added" : "income_added", {
      category: txn.expenseCategory || "unknown",
      isFirst:  transactions.length === 0,
    });
    const updated = [...transactions, txn];
    setTransactions(updated);
    if (txn.cardId) {
      setDebtState(p => ({...p, debts:p.debts.map(d=>d.id!==txn.cardId?d:{...d,currentBalance:d.currentBalance+Math.abs(txn.amount)})}));
    }
  };

  const delTx = (id) => {
    const txn = transactions.find(t=>t.id===id);
    setTransactions(prev=>prev.filter(t=>t.id!==id));
    // If it was a credit card payment, reverse the balance
    if (txn?.cardId) {
      setDebtState(p=>({...p,debts:p.debts.map(d=>d.id!==txn.cardId?d:{...d,currentBalance:Math.max(0,d.currentBalance-Math.abs(txn.amount))})}));
    }
    // If it was a goal contribution, subtract from the goal's saved amount
    if (txn?.isGoalContribution && txn?.goalId) {
      const amt = Math.abs(txn.amount);
      setGoals(prev => prev.map(g => g.id===txn.goalId ? {...g, saved:Math.max(0, g.saved-amt)} : g));
    }
  };

  // Apply recurring to month
  const applyRecurring = (targetMonth) => {
    const toAdd = recurring.filter(r => r.active && !transactions.some(t => t.date.startsWith(targetMonth) && t.recurringId===r.id));
    toAdd.forEach(r => {
      const day = Math.min(r.dayOfMonth, getDaysInMonth(targetMonth));
      const dateStr = `${targetMonth}-${String(day).padStart(2,"0")}`;
      addTx({ id:Date.now()+Math.random(), date:dateStr, amount: r.type==="income" ? r.amount : -r.amount,
        description:r.name, expenseCategory:r.category, category:halalBucket[r.category]||"Want",
        icon:r.icon, cardId:null, recurring:true, recurringId:r.id });
    });
  };

  // Confirm a recurring payment for a month — adds the transaction
  const showUndo = (label, action) => {
    setUndoToast({ label, action });
    setTimeout(() => setUndoToast(null), 4000);
  };

  const confirmRecurring = (r, targetMonth) => {
    const key = `${r.id}_${targetMonth}`;
    if (recurringStatus[key] === 'confirmed') return;
    const day = Math.min(r.dayOfMonth, getDaysInMonth(targetMonth));
    const dateStr = `${targetMonth}-${String(day).padStart(2,"0")}`;
    const txId = Date.now()+Math.random();
    addTx({ id:txId, date:dateStr, amount: r.type==="income" ? r.amount : -r.amount,
      description:r.name, expenseCategory:r.category, category:halalBucket[r.category]||"Want",
      icon:r.icon, cardId:null, recurring:true, recurringId:r.id });
    setRecurringStatus(prev => ({...prev, [key]:'confirmed'}));
    showUndo(`✓ ${r.name} marked as paid`, () => {
      setTransactions(prev => prev.filter(t => t.id !== txId));
      setRecurringStatus(prev => { const n={...prev}; delete n[key]; return n; });
    });
  };

  const skipRecurring = (r, targetMonth) => {
    const key = `${r.id}_${targetMonth}`;
    setRecurringStatus(prev => ({...prev, [key]:'skipped'}));
    showUndo(`— ${r.name} skipped`, () => {
      setRecurringStatus(prev => { const n={...prev}; delete n[key]; return n; });
    });
  };

  const getRecurStatus = (r, targetMonth) => {
    const key = `${r.id}_${targetMonth}`;
    if (recurringStatus[key]) return recurringStatus[key];
    // also check if a transaction with this recurringId already exists for this month
    if (transactions.some(t => t.recurringId===r.id && t.date.startsWith(targetMonth))) return 'confirmed';
    return 'pending';
  };

  // Selected day transactions
  const selectedDateStr = selDay ? `${month}-${String(selDay).padStart(2,"0")}` : null;
  const dayTxns = selectedDateStr ? transactions.filter(t=>t.date===selectedDateStr) : [];

  // AI suggestions (replaced by rule-based moneyInsights below)
  // const loadSuggestions = async () => { ... };

  // Spending by category — memoized
  const catSorted = useMemo(() => {
    const catSpending = {};
    monthTxns.filter(t=>t.amount<0).forEach(t => {
      const c = t.expenseCategory||"Other";
      catSpending[c] = (catSpending[c]||0)+Math.abs(t.amount);
    });
    return Object.entries(catSpending).sort((a,b)=>b[1]-a[1]);
  }, [monthTxns]);

  // Previous month transactions — for spending delta per category
  const prevMonthStr = useMemo(() => {
    const [yr, mo] = month.split("-").map(Number);
    return mo === 1 ? `${yr-1}-12` : `${yr}-${String(mo-1).padStart(2,"0")}`;
  }, [month]);
  const prevMonthCatSpend = useMemo(() => {
    const map = {};
    transactions.filter(t => t.date.startsWith(prevMonthStr) && t.amount < 0).forEach(t => {
      const c = t.expenseCategory || "Other";
      map[c] = (map[c] || 0) + Math.abs(t.amount);
    });
    return map;
  }, [transactions, prevMonthStr]);

  // Rule-based Money Insights — no AI, no server, all local
  const moneyInsights = useMemo(() => {
    if (monthTxns.length < 3) return [];
    const insights = [];

    // Top spending category
    if (catSorted.length > 0) {
      const [topCat, topAmt] = catSorted[0];
      const pct = Math.round((topAmt / (monthExpenses || 1)) * 100);
      const c = getCat(topCat);
      insights.push({ icon: c.icon, title: `${topCat} is your top expense`, detail: `${pct}% of spending this month — ${fmt(topAmt)}` });
    }

    // Wants vs Needs breakdown
    let needs = 0, wants = 0, sadaqah = 0;
    monthTxns.filter(t => t.amount < 0).forEach(t => {
      const bucket = halalBucket[t.expenseCategory] || "Want";
      const amt = Math.abs(t.amount);
      if (bucket === "Need") needs += amt;
      else if (bucket === "Sadaqah") sadaqah += amt;
      else wants += amt;
    });
    const wantsNeedsTotal = needs + wants + sadaqah || 1;
    const needsPct = Math.round((needs / wantsNeedsTotal) * 100);
    const wantsPct = Math.round((wants / wantsNeedsTotal) * 100);
    if (needs > 0 || wants > 0) {
      insights.push({
        icon: "⚖️",
        title: `${needsPct}% Needs · ${wantsPct}% Wants`,
        detail: needsPct >= 70 ? "Most spending is on essentials — great discipline." : "Consider shifting more spending toward needs.",
      });
    }

    // Sadaqah given
    if (sadaqah > 0) {
      insights.push({ icon: "🤲", title: `${fmt(sadaqah)} given in Sadaqah`, detail: "JazakAllah khair — may it be accepted." });
    }

    // vs last month
    const [yr, mo] = month.split("-").map(Number);
    const prevMonth = mo === 1 ? `${yr - 1}-12` : `${yr}-${String(mo - 1).padStart(2, "0")}`;
    const prevExp = transactions.filter(t => t.date.startsWith(prevMonth) && t.amount < 0).reduce((s, t) => s + Math.abs(t.amount), 0);
    if (prevExp > 0 && monthExpenses > 0) {
      const diff = Math.round(((monthExpenses - prevExp) / prevExp) * 100);
      insights.push({
        icon: diff > 0 ? "📈" : "📉",
        title: `${Math.abs(diff)}% ${diff > 0 ? "more" : "less"} than last month`,
        detail: diff > 0 ? `Spending up ${fmt(monthExpenses - prevExp)} from last month.` : `Spending down ${fmt(prevExp - monthExpenses)} — good progress.`,
      });
    }

    // Savings rate
    if (monthIncome > 0) {
      const rate = Math.round((monthSaved / monthIncome) * 100);
      insights.push({
        icon: rate >= 20 ? "🌱" : "💡",
        title: `${Math.max(0, rate)}% savings rate this month`,
        detail: rate >= 20 ? "Above the recommended 20% savings target — keep it up." : rate > 0 ? "Aim to save at least 20% of income each month." : "Spending exceeded income this month — review your budget.",
      });
    }

    return insights;
  }, [monthTxns, monthExpenses, monthIncome, monthSaved, catSorted, transactions, month]);

  // Prev/next month
  const monthsAgo = (ym) => {
    const [y,m] = ym.split("-").map(Number);
    const now = new Date();
    return (now.getFullYear() - y) * 12 + (now.getMonth() + 1 - m);
  };

  const changeMonth = (dir) => {
    setShowAllTxns(false);
    const [y,m] = month.split("-").map(Number);
    const d = new Date(y, m-1+dir, 1);
    const next = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}`;
    // Free users limited to 2 months back
    if (dir < 0 && monthsAgo(next) >= 2 && !proUser) {
      openUpgrade("month_history");
      return;
    }
    setMonth(next);
    setSelDay(null);
  };

  const DESKTOP_TABS = [
    { id:"overview",  label:"Home",     Icon:Home },
    { id:"calendar",  label:"Daily",    Icon:CalendarDays },
    { id:"tools",     label:"Wealth",   Icon:Sprout },
    { id:"settings",  label:"Settings", Icon:Settings },
  ];

  // To add a new Wealth tool: append one object here. Nothing else needs to change.
  const TOOL_TABS = [
    { id:"zakat",     emoji:"🌙", label:"Zakat",     subtitle:"Annual obligation",     pro:false,       iconBg:"rgba(212,175,55,0.20)",  gradient:"radial-gradient(ellipse at center, rgba(212,175,55,0.32) 0%, rgba(212,175,55,0.10) 55%, #1e1e28 100%)" },
    { id:"goals",     emoji:"🎯", label:"Goals",     subtitle:"Savings targets",       pro:false,       iconBg:"rgba(61,214,140,0.18)",  gradient:"radial-gradient(ellipse at center, rgba(61,214,140,0.26) 0%, rgba(61,214,140,0.08) 55%, #1e1e28 100%)" },
    { id:"recurring", emoji:"🔁", label:"Recurring", subtitle:"Bills & subscriptions",                  iconBg:"rgba(192,202,220,0.18)", gradient:"radial-gradient(ellipse at center, rgba(192,202,220,0.28) 0%, rgba(192,202,220,0.09) 55%, #1e1e28 100%)" },
    { id:"debts",     emoji:"💳", label:"Debts",     subtitle:"What you owe",          pro:false,       iconBg:"rgba(240,82,82,0.16)",   gradient:"radial-gradient(ellipse at center, rgba(240,82,82,0.24) 0%, rgba(240,82,82,0.07) 55%, #1e1e28 100%)" },
    { id:"barakah",   emoji:"⭐", label:"Barakah",   subtitle:"Daily habits",          pro:false,       iconBg:"rgba(167,139,250,0.18)", gradient:"radial-gradient(ellipse at center, rgba(167,139,250,0.26) 0%, rgba(167,139,250,0.08) 55%, #1e1e28 100%)" },
    { id:"learn",     emoji:"📚", label:"Learn",     subtitle:"Resources & coaching",  comingSoon:true, iconBg:"rgba(96,165,250,0.14)",  gradient:"radial-gradient(ellipse at center, rgba(96,165,250,0.20) 0%, rgba(96,165,250,0.06) 55%, #1e1e28 100%)" },
  ];

  // Onboarding complete handler
  const completeOnboarding = (data) => {
    setSettings(data);
    setOnboarded(true);
    setCurrencySymbol(data.currencySymbol||"CA$");
    setBudgetData(load(BUDGETS_KEY, null));
    setBarakahEnabled(load("bb_barakah_enabled", null) === "true");
  };

  const enableBarakah = () => {
    save("bb_barakah_enabled", "true");
    setBarakahEnabled(true);
  };

  // Route-based pages — no auth/onboarding required
  const path = window.location.pathname;
  const _qs = new URLSearchParams(window.location.search);
  const _stripeReturn = _qs.get("success") === "1" && _qs.get("session_id");
  if (path === "/privacy" || path === "/privacy/") return <PrivacyPage />;
  if (path === "/terms"   || path === "/terms/")   return <TermsPage />;
  if (path === "/support" || path === "/support/") return <SupportPage />;
  // Skip landing page if Stripe is redirecting back with a session — app must handle token verification
  if (!_stripeReturn && (path === "/" || path === "/home" || path === "/home/")) return <LandingPage />;

  if (!onboarded) return <Onboarding onComplete={completeOnboarding} />;

  return (
    <div style={{ minHeight:"100vh", background:C.bg, color:C.text, fontFamily:"'DM Sans',sans-serif", paddingBottom: isMobile ? "calc(env(safe-area-inset-bottom, 0px) + 90px)" : 40 }}>
      <FontLink />

      {/* ── HEADER ── */}
      {(() => {
        const h = new Date().getHours();
        const greeting = h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening";
        const name = settings?.name;

        const base = {
          background:"rgba(14,14,19,0.95)",
          paddingTop:"calc(env(safe-area-inset-top, 0px) + 14px)",
          paddingBottom:"14px",
          position:"sticky", top:0, zIndex:50,
          backdropFilter:"blur(20px)", WebkitBackdropFilter:"blur(20px)",
        };

        // AI CHAT BUTTON — hidden until native app feedback confirms demand
        // const aiBtn = (
        //   <button onClick={() => proUser ? setShowChat(true) : openUpgrade("ai_chat")}
        //     style={{ width:38, height:38, borderRadius:"50%", flexShrink:0,
        //       background: proUser ? C.surface2 : C.purpleDim,
        //       border:`1.5px solid ${proUser ? C.border2 : C.purpleDim}`,
        //       fontSize:17, cursor:"pointer",
        //       display:"flex", alignItems:"center", justifyContent:"center" }}>
        //     <span id="tour-ai">{proUser ? "💬" : "✨"}</span>
        //   </button>
        // );
        const aiBtn = null;

        // ── Desktop: always full header with nav ──────────────
        if (!isMobile) return (
          <div style={{ ...base, paddingLeft:"32px", paddingRight:"32px",
            display:"flex", alignItems:"center", justifyContent:"space-between" }}>
            <div style={{ display:"flex", alignItems:"center", gap:12 }}>
              <img src="/favicon_io/android-chrome-192x192.png" alt="logo"
                style={{ width:36, height:36, borderRadius:10, flexShrink:0 }} />
              <div>
                <div style={{ display:"flex", alignItems:"center", gap:7 }}>
                  <span style={{ fontWeight:800, fontSize:17, color:C.text, letterSpacing:"-0.03em" }}>barakah</span>
                  <span style={{ fontWeight:400, fontSize:17, color:C.green, letterSpacing:"-0.02em" }}>budget</span>
                  <ProBadge />
                </div>
                <div style={{ fontSize:11, color:C.text3, marginTop:2 }}>
                  {name ? `${greeting}, ${name}` : greeting}
                </div>
              </div>
            </div>
            <div style={{ display:"flex", alignItems:"center", gap:2,
              background:C.surface2, borderRadius:12, padding:"4px", border:`1px solid ${C.border}` }}>
              {DESKTOP_TABS.map(({ id, label, Icon }) => {
                const isActive = tab === id;
                return (
                  <button key={id} onClick={() => { setTab(id); if (id !== "tools") setToolsTab(null); }}
                    style={{ display:"flex", alignItems:"center", gap:7,
                      padding:"8px 18px", borderRadius:9, border:"none", cursor:"pointer",
                      background: isActive ? C.surface3 : "transparent",
                      color: isActive ? C.purple : C.text3,
                      fontSize:13, fontWeight: isActive ? 700 : 500,
                      transition:"all 0.15s" }}>
                    <Icon size={15} strokeWidth={isActive ? 2.2 : 1.8} />
                    {label}
                  </button>
                );
              })}
            </div>
            {aiBtn}
          </div>
        );

        // ── Mobile calendar: minimal title header ─────────────
        if (tab === "calendar") return (
          <div style={{ ...base, paddingLeft:"16px", paddingRight:"16px",
            display:"flex", alignItems:"center", justifyContent:"space-between" }}>
            <span style={{ fontWeight:800, fontSize:20, color:C.text, letterSpacing:"-0.02em" }}>
              Daily
            </span>
            {aiBtn}
          </div>
        );

        // ── Mobile tools / settings: minimal title ────────────
        if (tab === "tools" || tab === "settings") return (
          <div style={{ ...base, paddingLeft:"16px", paddingRight:"16px",
            display:"flex", alignItems:"center", justifyContent:"space-between" }}>
            <span style={{ fontWeight:800, fontSize:20, color:C.text, letterSpacing:"-0.02em" }}>
              {tab === "tools" ? "Wealth" : "Settings"}
            </span>
            {aiBtn}
          </div>
        );

        // ── Mobile home: full hub header ──────────────────────
        return (
          <div style={{ ...base, paddingLeft:"16px", paddingRight:"16px",
            display:"flex", alignItems:"center", justifyContent:"space-between" }}>
            <div style={{ display:"flex", alignItems:"center", gap:12 }}>
              <img src="/favicon_io/android-chrome-192x192.png" alt="logo"
                style={{ width:36, height:36, borderRadius:10, flexShrink:0 }} />
              <div>
                <div style={{ display:"flex", alignItems:"center", gap:7 }}>
                  <span style={{ fontWeight:800, fontSize:17, color:C.text, letterSpacing:"-0.03em" }}>barakah</span>
                  <span style={{ fontWeight:400, fontSize:17, color:C.green, letterSpacing:"-0.02em" }}>budget</span>
                  <ProBadge />
                </div>
                <div style={{ fontSize:11, color:C.text3, marginTop:2 }}>
                  {name ? `${greeting}, ${name}` : greeting}
                </div>
              </div>
            </div>
            {aiBtn}
          </div>
        );
      })()}

      {/* ── MONTH ROW — home tab only; calendar has its own month nav inside content ── */}
      {tab === "overview" && (
        <div id="tour-month-nav" style={{
          display:"flex", alignItems:"center", justifyContent:"space-between",
          padding:"10px 16px 0",
          maxWidth:560, margin:"0 auto", boxSizing:"border-box",
        }}>
          <button onClick={()=>changeMonth(-1)}
            style={{ background:"rgba(255,255,255,0.05)", border:"none", borderRadius:10,
              color:C.text2, width:36, height:36, cursor:"pointer",
              display:"flex", alignItems:"center", justifyContent:"center" }}>
            <ChevronLeft size={18} />
          </button>

          <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:2 }}>
            <span style={{ color:C.text, fontSize:17, fontWeight:800, letterSpacing:"-0.02em" }}>
              {getMonthLabel(month)}
            </span>
            <span style={{ color:C.text3, fontSize:10, fontWeight:500 }}>
              {getHijriMonthLabel(month)}
            </span>
          </div>

          <button onClick={()=>changeMonth(1)}
            style={{ background:"rgba(255,255,255,0.05)", border:"none", borderRadius:10,
              color:C.text2, width:36, height:36, cursor:"pointer",
              display:"flex", alignItems:"center", justifyContent:"center" }}>
            <ChevronRight size={18} />
          </button>
        </div>
      )}


      <div style={{ maxWidth:560, margin:"0 auto", padding:"0 16px", boxSizing:"border-box" }}>

        {/* ── DEMO BANNER ── */}
        {!demoLoaded && transactions.length === 0 && (
          <div style={{ borderRadius:16, padding:"20px 18px", margin:"14px 0",
            background:"rgba(61,214,140,0.06)", border:"1px solid rgba(61,214,140,0.15)" }}>
            <div style={{ fontSize:13, color:C.green, fontWeight:700, letterSpacing:"0.06em", marginBottom:8 }}>GET STARTED</div>
            <div style={{ color:C.text, fontWeight:800, fontSize:18, letterSpacing:"-0.02em", marginBottom:6, lineHeight:1.3 }}>
              What's the last thing you paid for?
            </div>
            <div style={{ color:C.text2, fontSize:12, marginBottom:16, lineHeight:1.6 }}>
              Add it now — takes 10 seconds.
              Your data stays on your phone, always.
            </div>
            <button onClick={() => setShowAddDrawer(true)}
              style={{ width:"100%", background:`linear-gradient(135deg,${C.green},#22c55e)`,
                border:"none", borderRadius:12, color:"#0e0e13", padding:"14px",
                fontSize:14, fontWeight:800, cursor:"pointer", marginBottom:14 }}>
              + Add it now
            </button>
            {/* Change 3 — demo as tiny text link, not a button */}
            <div style={{ textAlign:"center" }}>
              <button onClick={loadDemo}
                style={{ background:"none", border:"none",
                  color:C.text3, fontSize:10, cursor:"pointer", padding:"4px",
                  textDecoration:"underline", opacity:0.6 }}>
                explore with demo data
              </button>
            </div>
          </div>
        )}
        {demoLoaded && (
          <div style={{ background:"rgba(251,191,36,0.06)", border:`1px solid rgba(251,191,36,0.2)`, borderRadius:10, padding:"8px 14px", margin:"8px 0 4px", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
            <span style={{ color:C.gold, fontSize:11, fontWeight:600 }}>✨ Demo mode — Ahmed Al-Farsi</span>
            <button onClick={clearDemo} style={{ background:"none", border:`1px solid rgba(251,191,36,0.3)`, color:C.gold, borderRadius:7, padding:"3px 10px", fontSize:10, cursor:"pointer" }}>Clear</button>
          </div>
        )}

        {isMobile && <div style={{ height:12 }} />}

        {/* ══════════════ OVERVIEW TAB ══════════════ */}
        {tab === "overview" && (
          <div className="fade-up">
            <NudgeBanner />

            {/* ── FIRST STEPS NUDGE — shown until user has 5+ transactions ── */}
            {!demoLoaded && transactions.length >= 1 && transactions.length <= 4 && (
              <div style={{
                background:"linear-gradient(135deg, rgba(61,214,140,0.1) 0%, rgba(61,214,140,0.04) 100%)",
                border:"1px solid rgba(61,214,140,0.2)", borderRadius:16,
                padding:"16px 18px", marginBottom:16,
                display:"flex", alignItems:"center", justifyContent:"space-between", gap:12,
              }}>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ color:C.green, fontWeight:700, fontSize:13, marginBottom:4 }}>
                    🌱 Good start — keep going
                  </div>
                  <div style={{ color:C.text2, fontSize:12, lineHeight:1.5 }}>
                    Add {5 - transactions.length} more to unlock your spending chart and category breakdown.
                  </div>
                  <div style={{ display:"flex", gap:4, marginTop:10 }}>
                    {[1,2,3,4,5].map(i => (
                      <div key={i} style={{
                        height:4, flex:1, borderRadius:99,
                        background: i <= transactions.length ? C.green : "rgba(255,255,255,0.08)",
                        transition:"background 0.3s",
                      }} />
                    ))}
                  </div>
                </div>
                <button onClick={() => setShowAddDrawer(true)} style={{
                  background:C.green, border:"none", color:"#0e0e13",
                  borderRadius:12, width:44, height:44, fontSize:22, fontWeight:800,
                  cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center",
                  flexShrink:0, boxShadow:"0 2px 12px rgba(61,214,140,0.35)",
                }}>+</button>
              </div>
            )}

            {/* Recovery card — shown when app looks empty but a backup exists */}
            {transactions.length === 0 && !!localStorage.getItem("bb_last_backup") && !demoLoaded && !recoveryCardDismissed && (
              <div style={{ background:"linear-gradient(135deg,rgba(167,139,250,0.1),rgba(167,139,250,0.05))",
                border:"1px solid rgba(167,139,250,0.25)", borderRadius:16, padding:"16px", marginBottom:12 }}>
                <div style={{ fontSize:22, marginBottom:8 }}>💾</div>
                <div style={{ color:C.text, fontWeight:700, fontSize:15, marginBottom:4 }}>Your data looks empty</div>
                <div style={{ color:C.text2, fontSize:12, lineHeight:1.6, marginBottom:14 }}>
                  Safari may have cleared it automatically. If you saved a backup file, you can restore everything now.
                </div>
                <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
                  <button onClick={() => setTab("settings")}
                    style={{ background:"rgba(167,139,250,0.2)", border:"1px solid rgba(167,139,250,0.3)",
                      borderRadius:10, color:C.purple, fontSize:12, fontWeight:700,
                      padding:"10px 16px", cursor:"pointer" }}>
                    Restore from backup file →
                  </button>
                  <button onClick={() => { localStorage.removeItem("bb_last_backup"); localStorage.removeItem("bb_tx_count_at_backup"); setRecoveryCardDismissed(true); }}
                    style={{ background:"none", border:`1px solid ${C.border2}`, borderRadius:10,
                      color:C.text3, fontSize:12, padding:"10px 16px", cursor:"pointer" }}>
                    Start fresh — I don't have a backup
                  </button>
                </div>
              </div>
            )}

            {/* ── BACKUP NUDGE — reappears every 7 days ── */}
            {(() => {
              const lastBackup = localStorage.getItem("bb_last_backup");
              const daysSinceBackup  = lastBackup ? Math.floor((Date.now() - parseInt(lastBackup)) / 86400000) : null;
              const daysSinceDismiss = nudgeDismissedAt ? Math.floor((Date.now() - parseInt(nudgeDismissedAt)) / 86400000) : null;
              const dismissedRecently = daysSinceDismiss !== null && daysSinceDismiss < 7;
              const needsNudge = !dismissedRecently && (
                (!lastBackup && transactions.length >= 5) ||
                (daysSinceBackup >= 7 && transactions.length > 0)
              );
              if (!needsNudge) return null;
              return (
                <div style={{ background:"rgba(245,166,35,0.07)", border:"1px solid rgba(245,166,35,0.2)",
                  borderRadius:16, padding:"12px 16px", marginBottom:12,
                  display:"flex", alignItems:"center", gap:12 }}>
                  <div style={{ fontSize:20 }}>💾</div>
                  <div style={{ flex:1, fontSize:12, color:C.text2, lineHeight:1.5 }}>
                    {lastBackup ? `No backup in ${daysSinceBackup} days.` : "You haven't backed up yet."} Save your data before it disappears.
                  </div>
                  <button onClick={doExport}
                    style={{ background:C.amber, border:"none", color:"#0e0e13", borderRadius:8,
                      padding:"7px 12px", fontSize:11, fontWeight:700, cursor:"pointer", flexShrink:0 }}>
                    Save backup now
                  </button>
                  <button onClick={() => { const now = Date.now().toString(); localStorage.setItem("bb_backup_nudge_dismissed", now); setNudgeDismissedAt(now); }}
                    style={{ background:"none", border:"none", color:C.text3, cursor:"pointer", fontSize:16, padding:"0 2px" }}>×</button>
                </div>
              );
            })()}

            {/* ── WEEKLY REVIEW CARD ── */}
            {(() => {
              const lastReview = localStorage.getItem("bb_last_review");
              const daysSince = lastReview
                ? Math.floor((Date.now() - parseInt(lastReview)) / (1000 * 60 * 60 * 24))
                : null;
              const showReview = isCurrentMonth && transactions.length >= 3 && (daysSince === null || daysSince >= 6);

              // weekReviewed check BEFORE showReview — setting bb_last_review makes showReview false immediately
              if (weekReviewed && isCurrentMonth) return (
                <div style={{ background:"rgba(61,214,140,0.06)", border:"1px solid rgba(61,214,140,0.2)",
                  borderRadius:16, padding:"20px 18px", marginBottom:14,
                  display:"flex", alignItems:"center", gap:14, animation:"fadeUp 0.3s ease" }}>
                  <div style={{ fontSize:28 }}>🌿</div>
                  <div>
                    <div style={{ color:C.green, fontWeight:700, fontSize:14, marginBottom:2 }}>
                      Reviewed ✓
                    </div>
                    <div style={{ color:C.text2, fontSize:12 }}>
                      JazakAllah khair — see you next week.
                    </div>
                  </div>
                </div>
              );

              if (!showReview) return null;

              trackReviewShown(); // fires once per review cycle, not on every render

              const reviewWeekStart = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().slice(0,10);
              const reviewWeekTxns  = transactions.filter(t => t.date >= reviewWeekStart && t.amount < 0);
              const weekTotal = reviewWeekTxns.reduce((s,t) => s + Math.abs(t.amount), 0);
              const byCat = {};
              reviewWeekTxns.forEach(t => {
                const c = t.category || "Other";
                byCat[c] = (byCat[c] || 0) + Math.abs(t.amount);
              });
              const topCats = Object.entries(byCat).sort((a,b) => b[1]-a[1]).slice(0,3);

              return (
                <div style={{ background:"linear-gradient(135deg,rgba(61,214,140,0.08),rgba(167,139,250,0.06))",
                  border:"1px solid rgba(61,214,140,0.2)", borderRadius:16, padding:"16px",
                  marginBottom:12, animation:"fadeUp 0.3s ease" }}>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:12 }}>
                    <div>
                      <div style={{ fontSize:11, color:C.green, fontWeight:700, letterSpacing:"0.06em", marginBottom:4 }}>
                        WEEKLY REVIEW
                      </div>
                      <div style={{ fontSize:17, fontWeight:800, color:C.text, letterSpacing:"-0.02em" }}>
                        Your week in 60 seconds
                      </div>
                    </div>
                    <div style={{ fontSize:22 }}>🌿</div>
                  </div>

                  <div style={{ background:"rgba(0,0,0,0.2)", borderRadius:12, padding:"12px 14px", marginBottom:12 }}>
                    <div style={{ fontSize:11, color:C.text3, marginBottom:4 }}>Total spent this week</div>
                    <div style={{ fontSize:24, fontWeight:800, color:C.text, letterSpacing:"-0.02em" }}>
                      {fmt(weekTotal)}
                    </div>
                    {topCats.length > 0 && (
                      <div style={{ display:"flex", gap:6, marginTop:8, flexWrap:"wrap" }}>
                        {topCats.map(([cat, amt]) => (
                          <span key={cat} style={{ background:"rgba(255,255,255,0.06)", borderRadius:20,
                            padding:"3px 10px", fontSize:10, color:C.text2 }}>
                            {cat} · {fmt(amt)}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  <button
                    onClick={() => {
                      localStorage.setItem("bb_last_review", Date.now().toString());
                      track("weekly_review_completed");
                      logInfo("transactions", "weekly_review_completed", { weekTotal });
                      setWeekReviewed(true);
                    }}
                    style={{ width:"100%", background:`linear-gradient(135deg,${C.green},#22c55e)`,
                      border:"none", borderRadius:12, color:"#0e0e13", padding:"13px",
                      fontSize:13, fontWeight:800, cursor:"pointer" }}>
                    ✓ Mark Week as Reviewed
                  </button>
                </div>
              );
            })()}





            {/* Big spending number — only show when there is data */}
            {/* Empty state for existing users viewing a month with no data */}
            {transactions.length > 0 && monthTxns.length === 0 && (
              <div style={{ textAlign:"center", padding:"32px 0 20px" }}>
                <div style={{ fontSize:32, marginBottom:12 }}>📭</div>
                <div style={{ color:C.text, fontWeight:700, fontSize:15, marginBottom:6 }}>
                  No expenses for this month
                </div>
                <div style={{ color:C.text3, fontSize:12, marginBottom:20, lineHeight:1.6 }}>
                  Missed some days? No stress — add them now.
                </div>
                <button onClick={() => setShowAddDrawer(true)}
                  style={{ width:"100%", background:`linear-gradient(135deg,${C.green},#22c55e)`,
                    border:"none", borderRadius:14, color:"#0e0e13",
                    fontSize:15, fontWeight:800, padding:"16px",
                    cursor:"pointer", boxShadow:"0 4px 20px rgba(61,214,140,0.25)" }}>
                  + Add Expense
                </button>
              </div>
            )}

            {monthTxns.length > 0 && <div id="tour-spending" style={{
              background:"radial-gradient(ellipse at top left, rgba(61,214,140,0.06) 0%, #16161e 60%)",
              border:`1px solid ${C.border}`,
              borderRadius:16, padding:"16px 16px 12px", marginTop:12, marginBottom:20,
            }}>
              {/* Header row */}
              <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", marginBottom:4 }}>
                <span style={{ fontSize:11, fontWeight:700, color:C.text3, letterSpacing:"0.08em", textTransform:"uppercase" }}>
                  {showWeekly ? "Spent This Week" : "Spent This Month"}
                </span>
                <button onClick={()=>setShowAddDrawer(true)} style={{
                  background:C.green, border:"none", color:"#0e0e13",
                  borderRadius:14, width:40, height:40, fontSize:22, fontWeight:800, cursor:"pointer",
                  display:"flex", alignItems:"center", justifyContent:"center",
                  flexShrink:0, boxShadow:"0 2px 14px rgba(61,214,140,0.45)",
                }}>+</button>
              </div>

              {/* Big number */}
              <div key={month} style={{ fontSize:40, fontWeight:800, color:C.text, letterSpacing:"-0.04em", marginBottom:8, lineHeight:1, animation:"countUp 0.4s ease forwards" }}>
                {fmt(showWeekly ? weekExpenses : monthExpenses)}
              </div>

              {/* Saved / income chips */}
              {!showWeekly && (
                <div style={{ display:"flex", gap:6, alignItems:"center", marginBottom:14, flexWrap:"wrap" }}>
                  <span style={{
                    fontSize:12, fontWeight:700, borderRadius:20, padding:"3px 10px",
                    color: monthSaved>=0 ? C.green : C.red,
                    background: monthSaved>=0 ? "rgba(61,214,140,0.12)" : "rgba(240,82,82,0.12)",
                  }}>
                    {monthSaved>=0 ? "+" : "-"}{fmt(Math.abs(monthSaved))} {monthSaved>=0 ? "saved" : "over"}
                  </span>
                  <span style={{ fontSize:12, fontWeight:600, color:C.text3, background:"rgba(255,255,255,0.05)", borderRadius:20, padding:"3px 10px" }}>
                    {fmt(monthIncome)} in
                  </span>
                </div>
              )}
              {showWeekly && <WeeklyChart transactions={transactions} />}

              {/* Month progress bar — current month only */}
              {!showWeekly && isCurrentMonth && (() => {
                const dayNum = new Date().getDate();
                const totalDays = getDaysInMonth(month);
                const pct = (dayNum / totalDays) * 100;
                return (
                  <div style={{ marginBottom:14 }}>
                    <div style={{ display:"flex", justifyContent:"space-between", marginBottom:5 }}>
                      <span style={{ fontSize:10, color:C.text3 }}>Day {dayNum} of {totalDays}</span>
                      <span style={{ fontSize:10, color:C.text3 }}>{Math.round(pct)}% through month</span>
                    </div>
                    <div style={{ height:3, background:"rgba(255,255,255,0.06)", borderRadius:2 }}>
                      <div style={{ height:"100%", width:`${pct}%`, background:"rgba(255,255,255,0.18)", borderRadius:2, transition:"width 0.4s ease" }} />
                    </div>
                  </div>
                );
              })()}

              {/* Chart — always shown */}
              {!showWeekly && <SpendingChart transactions={transactions} month={month} />}

              {!showWeekly && (
                <div style={{ display:"flex", gap:12, marginTop:8, marginBottom: monthTxns.length < 5 ? 10 : 0 }}>
                  <div style={{ display:"flex", alignItems:"center", gap:5 }}>
                    <div style={{ width:8, height:8, borderRadius:"50%", background:C.purple }} />
                    <span style={{ fontSize:10, color:C.text3 }}>This period</span>
                  </div>
                </div>
              )}

              {/* Category mini-bars — shown when data is sparse (supplements the chart) */}
              {!showWeekly && monthTxns.length > 0 && monthTxns.length < 5 && catSorted.length > 0 && (() => {
                const top = catSorted.slice(0, 4);
                const max = top[0][1];
                return (
                  <div style={{ marginTop:4, display:"flex", flexDirection:"column", gap:8 }}>
                    {top.map(([catId, amt]) => {
                      const c = getCat(catId);
                      const pct = Math.round((amt / max) * 100);
                      return (
                        <div key={catId} style={{ display:"flex", alignItems:"center", gap:10 }}>
                          <div style={{ fontSize:14, width:22, textAlign:"center", flexShrink:0 }}>{c.icon}</div>
                          <div style={{ flex:1 }}>
                            <div style={{ display:"flex", justifyContent:"space-between", marginBottom:3 }}>
                              <span style={{ fontSize:11, color:C.text2, fontWeight:600 }}>{catId}</span>
                              <span style={{ fontSize:11, color:C.text, fontWeight:700 }}>{fmt(amt)}</span>
                            </div>
                            <div style={{ height:5, background:"rgba(255,255,255,0.06)", borderRadius:99, overflow:"hidden" }}>
                              <div style={{ height:"100%", width:`${pct}%`, background:c.color, borderRadius:99,
                                animation:"barGrow 0.6s cubic-bezier(0.22,1,0.36,1) forwards" }} />
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>}

            {/* ── BUDGET REMAINING WIDGET — Pro only ── */}
            {proUser && budgetData?.income > 0 && budgetData?.budgets && catSorted.length > 0 && monthExpenses > 0 && (() => {
              // Only show cats that have a budget set
              const rows = catSorted
                .map(([cat, spent]) => {
                  const budget = budgetData.budgets[cat] || 0;
                  if (!budget) return null;
                  const remaining = budget - spent;
                  const pct = Math.min(100, (spent / budget) * 100);
                  const pctLeft = Math.max(0, 100 - pct);
                  const status = pct >= 100 ? "over" : pct >= 75 ? "warn" : "good";
                  return { cat, spent, budget, remaining, pct, pctLeft, status };
                })
                .filter(Boolean)
                .sort((a,b) => b.pct - a.pct); // most critical first

              if (!rows.length) return null;

              const totalBudget = rows.reduce((s,r) => s + r.budget, 0);
              const totalSpent  = rows.reduce((s,r) => s + r.spent, 0);
              const totalLeft   = totalBudget - totalSpent;
              const daysInMonth = new Date(month.slice(0,4), month.slice(5,7), 0).getDate();
              const today       = new Date().getDate();
              const monthProgress = month === new Date().toISOString().slice(0,7)
                ? Math.round((today / daysInMonth) * 100) : 100;

              return (
                <div style={{ marginBottom:14 }}>
                  {/* Header */}
                  <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:10 }}>
                    <div>
                      <div style={{ color:C.text, fontWeight:700, fontSize:14 }}>
                        {totalLeft >= 0 ? "💰 Budget Remaining" : "Budget stretched"}
                      </div>
                      <div style={{ color:C.text3, fontSize:11, marginTop:1 }}>
                        {monthProgress}% through the month
                      </div>
                    </div>
                    <div style={{ textAlign:"right" }}>
                      <div style={{ color: totalLeft >= 0 ? C.green : C.amber, fontWeight:800, fontSize:20 }}>
                        {totalLeft >= 0 ? "+" : ""}{fmt(totalLeft)}
                      </div>
                      <div style={{ color:C.text3, fontSize:10 }}>
                        of {fmt(totalBudget)} total
                      </div>
                    </div>
                  </div>

                  {/* Month progress line */}
                  <div style={{ marginBottom:12, position:"relative" }}>
                    <div style={{ height:4, background:C.surface2, borderRadius:99, overflow:"visible", position:"relative" }}>
                      <div style={{ height:"100%", borderRadius:99, width:`${totalLeft>=0 ? Math.min(100,(totalSpent/totalBudget)*100) : 100}%`,
                        background: totalLeft>=0 ? `linear-gradient(90deg,${C.green},#22c55e)` : C.red,
                        transition:"width 0.6s ease" }} />
                      {/* Day marker */}
                      {month === new Date().toISOString().slice(0,7) && (
                        <div style={{ position:"absolute", top:-4, left:`${monthProgress}%`,
                          transform:"translateX(-50%)", width:2, height:12,
                          background:C.amber, borderRadius:99 }} />
                      )}
                    </div>
                    <div style={{ display:"flex", justifyContent:"space-between", marginTop:4 }}>
                      <span style={{ color:C.text3, fontSize:9 }}>Spent: {fmt(totalSpent)}</span>
                      {month === new Date().toISOString().slice(0,7) && (
                        <span style={{ color:C.amber, fontSize:9 }}>▲ Today (day {today})</span>
                      )}
                      <span style={{ color:C.text3, fontSize:9 }}>Budget: {fmt(totalBudget)}</span>
                    </div>
                  </div>

                  {/* Category rows */}
                  <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                    {rows.slice(0,5).map(r => {
                      const c = getCat(r.cat);
                      const barColor = r.status==="over" ? C.amber : r.status==="warn" ? C.amber : C.green;
                      return (
                        <div key={r.cat} style={{
                          background: r.status==="over"
                            ? "rgba(245,166,35,0.05)"
                            : r.status==="warn"
                              ? "rgba(245,166,35,0.04)"
                              : C.surface,
                          border:`1px solid ${r.status==="over" ? "rgba(245,166,35,0.18)" : r.status==="warn" ? "rgba(245,166,35,0.12)" : C.border}`,
                          borderRadius:12, padding:"10px 14px",
                        }}>
                          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:6 }}>
                            <span style={{ color:C.text, fontSize:13 }}>{c.icon} {r.cat}</span>
                            <div style={{ textAlign:"right" }}>
                              {r.status === "over" ? (
                                <span style={{ color:C.amber, fontWeight:700, fontSize:13 }}>
                                  {fmt(Math.abs(r.remaining))} over
                                </span>
                              ) : (
                                <span style={{ color: r.status==="warn" ? C.amber : C.green, fontWeight:700, fontSize:13 }}>
                                  {fmt(r.remaining)} left
                                </span>
                              )}
                              <span style={{ color:C.text3, fontSize:10, display:"block" }}>
                                {fmt(r.spent)} / {fmt(r.budget)}
                              </span>
                            </div>
                          </div>
                          {/* Remaining bar — shows what's LEFT not what's spent */}
                          <div style={{ height:5, background:C.surface2, borderRadius:99, overflow:"hidden" }}>
                            <div style={{
                              height:"100%", borderRadius:99,
                              width:`${r.pctLeft}%`,
                              background: barColor,
                              transition:"width 0.6s cubic-bezier(0.22,1,0.36,1)",
                              boxShadow: r.status==="over" ? "none" : `0 0 6px ${barColor}44`,
                            }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {rows.length > 5 && (
                    <div style={{ color:C.text3, fontSize:11, textAlign:"center", marginTop:8 }}>
                      +{rows.length - 5} more categories below ↓
                    </div>
                  )}
                </div>
              );
            })()}



            {/* Category breakdown — only show when there is data */}
            {monthTxns.length > 0 && catSorted.length > 0 && (
              <Card id="tour-categories" style={{ marginBottom:16 }}>
                <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:14 }}>
                  <span style={{ fontSize:11, fontWeight:700, color:C.text3, letterSpacing:"0.08em", textTransform:"uppercase" }}>
                    Spending by Category
                  </span>
                  {proUser && budgetData && (
                    <span style={{ fontSize:10, color:C.text3 }}>vs. budget</span>
                  )}
                </div>
                {catSorted.map(([catId, amt], i) => {
                  const c = getCat(catId);
                  const budget = budgetData?.budgets?.[catId];
                  const hasBudget = budget && budget > 0;
                  const totalSpend = monthExpenses || 1;
                  const sharePct = Math.round((amt / totalSpend) * 100);
                  const pct = hasBudget ? Math.min(100, (amt/budget)*100) : Math.min(100, (amt/totalSpend)*100);
                  const over = hasBudget && amt > budget;
                  const barColor = over ? C.red : c.color;
                  const isLast = i === catSorted.length - 1;
                  return (
                    <div key={catId} style={{
                      padding:"12px 0",
                      borderBottom: isLast ? "none" : `1px solid ${C.border}`,
                    }}>
                      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
                        <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                          <div style={{ width:28, height:28, borderRadius:8, display:"flex", alignItems:"center", justifyContent:"center",
                            background:`${c.color}18`, fontSize:14 }}>
                            {c.icon}
                          </div>
                          <span style={{ color:C.text, fontSize:13, fontWeight:600 }}>{catId}</span>
                        </div>
                        <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                          {!hasBudget && (
                            <span style={{ fontSize:11, color:C.text3, fontWeight:500 }}>{sharePct}%</span>
                          )}
                          <span style={{ color: over ? C.red : C.text, fontWeight:700, fontSize:14 }}>{fmt(amt)}</span>
                          {hasBudget && (
                            <span style={{ color: over ? C.red : C.text3, fontSize:11 }}>/ {fmt(budget)}</span>
                          )}
                        </div>
                      </div>
                      <div style={{ background:C.surface2, borderRadius:99, height:5, overflow:"hidden" }}>
                        <div style={{
                          width:`${pct}%`, height:"100%", borderRadius:99,
                          background: barColor,
                          transition:"width 0.6s cubic-bezier(0.22,1,0.36,1)",
                          boxShadow: over ? `0 0 6px ${C.red}55` : `0 0 4px ${barColor}44`,
                        }} />
                      </div>
                      {/* Spending delta vs last month */}
                      {(() => {
                        const prevAmt = prevMonthCatSpend[catId] || 0;
                        if (prevAmt <= 0 || amt <= 0) return null;
                        const diff = amt - prevAmt;
                        if (Math.abs(diff) < 1) return null;
                        return (
                          <div style={{ fontSize:10, marginTop:4, color: diff > 0 ? C.red : C.green, fontWeight:600 }}>
                            {diff > 0 ? `↑ ${fmt(diff)} more than last month` : `↓ ${fmt(Math.abs(diff))} less than last month`}
                          </div>
                        );
                      })()}
                    </div>
                  );
                })}
                {proUser && !budgetData && (
                  <div style={{ borderTop:`1px solid ${C.border}`, paddingTop:12, marginTop:4 }}>
                    <div style={{ color:C.text3, fontSize:11, textAlign:"center" }}>
                      No budgets set — <button onClick={()=>setTab("settings")} style={{ background:"none", border:"none", color:C.green, fontSize:11, fontWeight:600, cursor:"pointer", padding:0 }}>set income & budgets</button>
                    </div>
                  </div>
                )}
              </Card>
            )}





            {/* Money Insights — rule-based, no AI, no server */}
            {monthTxns.length >= 3 && (
              <Card style={{ marginBottom:16 }}>
                <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:14 }}>
                  <span>📊</span>
                  <span style={{ color:C.text, fontSize:13, fontWeight:700 }}>Money Insights</span>
                </div>
                {moneyInsights.map((ins, i) => (
                  <div key={i} style={{ display:"flex", gap:10, padding:"9px 0", borderBottom: i < moneyInsights.length - 1 ? `1px solid ${C.border}` : "none" }}>
                    <div style={{ fontSize:20, flexShrink:0 }}>{ins.icon}</div>
                    <div style={{ flex:1 }}>
                      <div style={{ color:C.text, fontSize:13, fontWeight:700, marginBottom:3 }}>{ins.title}</div>
                      <div style={{ color:C.text2, fontSize:12, lineHeight:1.6 }}>{ins.detail}</div>
                    </div>
                  </div>
                ))}
              </Card>
            )}

            {/* Riba alert */}
            {debtState.debts.filter(d=>getDebt(d.type).haram&&d.currentBalance>0).length>0 && (
              <div style={{ background:"rgba(240,82,82,0.06)", border:`1px solid rgba(240,82,82,0.2)`, borderRadius:14, padding:"14px 16px", marginBottom:16 }}>
                <div style={{ color:C.red, fontSize:13, fontWeight:700, marginBottom:8 }}>⚠️ Active Riba Debts</div>
                {debtState.debts.filter(d=>getDebt(d.type).haram&&d.currentBalance>0).map(d=>{
                  const mi = (d.currentBalance*(d.rate/100))/12;
                  return (
                    <div key={d.id} style={{ display:"flex", justifyContent:"space-between", padding:"5px 0", fontSize:12 }}>
                      <span style={{ color:C.text2 }}>{d.name}</span>
                      <span style={{ color:C.red }}>~{fmt(mi)}/mo interest</span>
                    </div>
                  );
                })}
                <button onClick={()=>{setTab("tools");setToolsTab("debts");}} style={{ marginTop:10, background:C.redDim, border:`1px solid rgba(240,82,82,0.2)`, color:C.red, borderRadius:8, padding:"6px 14px", fontSize:11, fontWeight:600, cursor:"pointer" }}>
                  Track & eliminate →
                </button>
              </div>
            )}

            {/* ── NET WORTH MINI CARD ── */}
            {(()=>{
              const totalDebt = debtState.debts.reduce((s,d) => s + (d.currentBalance||0), 0);
              const netWorth  = appNetSavings - totalDebt;
              const isPos     = netWorth >= 0;
              return (
                <div style={{ marginBottom:16 }}>
                  <button onClick={() => setNwOpen(p => !p)} style={{
                    width:"100%", background:C.surface, border:`1px solid ${isPos ? "rgba(61,214,140,0.2)" : "rgba(240,82,82,0.2)"}`,
                    borderRadius:14, padding:"16px 18px", cursor:"pointer",
                    display:"flex", alignItems:"center", justifyContent:"space-between", gap:16
                  }}>
                    {/* Left: NET WORTH label + big number */}
                    <div style={{ minWidth:0 }}>
                      <div style={{ color:C.text3, fontSize:9, fontWeight:700, letterSpacing:"0.12em",
                        textTransform:"uppercase", marginBottom:5 }}>Net Worth</div>
                      <div style={{ color: isPos ? C.green : C.red, fontSize:24, fontWeight:800,
                        letterSpacing:"-0.03em", whiteSpace:"nowrap", lineHeight:1 }}>
                        {isPos ? "" : "−"}{fmt(Math.abs(netWorth))}
                      </div>
                    </div>
                    {/* Right: Assets on top, Debts below, chevron */}
                    <div style={{ display:"flex", alignItems:"center", gap:10, flexShrink:0 }}>
                      <div style={{ textAlign:"right" }}>
                        <div style={{ marginBottom:8 }}>
                          <div style={{ color:C.text3, fontSize:9, fontWeight:600, letterSpacing:"0.08em",
                            textTransform:"uppercase", marginBottom:2 }}>Assets</div>
                          <div style={{ color:C.green, fontSize:13, fontWeight:700,
                            whiteSpace:"nowrap" }}>{fmt(appNetSavings)}</div>
                        </div>
                        <div>
                          <div style={{ color:C.text3, fontSize:9, fontWeight:600, letterSpacing:"0.08em",
                            textTransform:"uppercase", marginBottom:2 }}>Debts</div>
                          <div style={{ color: totalDebt > 0 ? C.red : C.text3, fontSize:13, fontWeight:700,
                            whiteSpace:"nowrap" }}>{fmt(totalDebt)}</div>
                        </div>
                      </div>
                      <span style={{ color:C.text3, fontSize:12,
                        transform: nwOpen ? "rotate(180deg)" : "none",
                        transition:"transform 0.2s", display:"inline-block" }}>▾</span>
                    </div>
                  </button>

                  {nwOpen && (
                    <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderTop:"none", borderRadius:"0 0 14px 14px", padding:"12px 16px" }} className="fade-up">
                      {/* Goals breakdown */}
                      {goals.length > 0 && (
                        <div style={{ marginBottom:10 }}>
                          <div style={{ color:C.text3, fontSize:10, fontWeight:600, letterSpacing:"0.08em", textTransform:"uppercase", marginBottom:6 }}>Savings & Goals</div>
                          {goals.map(g => (
                            <div key={g.id} style={{ display:"flex", justifyContent:"space-between", padding:"4px 0", fontSize:12 }}>
                              <span style={{ color:C.text2 }}>{g.emoji} {g.name}</span>
                              <span style={{ color:C.green, fontWeight:600 }}>{fmt(g.saved)}</span>
                            </div>
                          ))}
                        </div>
                      )}
                      {/* Debts breakdown */}
                      {debtState.debts.length > 0 && (
                        <div>
                          <div style={{ color:C.text3, fontSize:10, fontWeight:600, letterSpacing:"0.08em", textTransform:"uppercase", marginBottom:6 }}>Debts</div>
                          {debtState.debts.map(d => (
                            <div key={d.id} style={{ display:"flex", justifyContent:"space-between", padding:"4px 0", fontSize:12 }}>
                              <span style={{ color:C.text2 }}>{d.name}</span>
                              <span style={{ color:C.red, fontWeight:600 }}>−{fmt(d.currentBalance)}</span>
                            </div>
                          ))}
                        </div>
                      )}
                      {goals.length === 0 && debtState.debts.length === 0 && (
                        <div style={{ color:C.text3, fontSize:12, textAlign:"center", padding:"8px 0" }}>Add goals and debts to see your full picture</div>
                      )}
                    </div>
                  )}
                </div>
              );
            })()}

          </div>
        )}

        {/* ══════════════ CALENDAR / DAILY TAB ══════════════ */}
        {tab === "calendar" && (
          <div className="fade-up">

            {/* Month nav — part of the calendar, not the header */}
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:16 }}>
              <button onClick={() => changeMonth(-1)}
                style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:10,
                  color:C.text2, width:36, height:36, cursor:"pointer",
                  display:"flex", alignItems:"center", justifyContent:"center" }}>
                {monthsAgo(month) >= 1 && !proUser ? "🔒" : <ChevronLeft size={18} />}
              </button>
              <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:2 }}>
                <span style={{ color:C.text, fontSize:17, fontWeight:800, letterSpacing:"-0.02em" }}>
                  {getMonthLabel(month)}
                </span>
                <span style={{ color:C.text3, fontSize:10, fontWeight:500 }}>
                  {getHijriMonthLabel(month)}
                </span>
              </div>
              <button onClick={() => changeMonth(1)}
                style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:10,
                  color:C.text2, width:36, height:36, cursor:"pointer",
                  display:"flex", alignItems:"center", justifyContent:"center" }}>
                <ChevronRight size={18} />
              </button>
            </div>

            {/* Month summary strip */}
            <div style={{ display:"flex", gap:8, marginBottom:16 }}>
              {[
                { label:"Income",  value: fmt(monthIncome),   color: C.green },
                { label:"Spent",   value: fmt(monthExpenses), color: C.red   },
                { label:"Net",     value: (monthSaved >= 0 ? "+" : "") + fmt(monthSaved), color: monthSaved >= 0 ? C.green : C.red },
              ].map(s => (
                <div key={s.label} style={{
                  flex:1, background:C.surface, borderRadius:12, padding:"10px 10px 8px",
                  display:"flex", flexDirection:"column", gap:3, textAlign:"center",
                }}>
                  <div style={{ fontSize:9, color:C.text3, fontWeight:600, letterSpacing:"0.08em", textTransform:"uppercase" }}>{s.label}</div>
                  <div style={{ fontSize:14, fontWeight:800, color:s.color, letterSpacing:"-0.02em" }}>{s.value}</div>
                </div>
              ))}
            </div>

            {/* Calendar — no Card wrapper so it breathes */}
            <div id="tour-calendar" style={{ marginBottom:24 }}>
              <CalendarView transactions={transactions} month={month} onDayClick={d=>{setSelDay(selDay===d?null:d);setShowAdd(false);}} selectedDay={selDay} />
            </div>

            {/* Monthly spending breakdown */}
            {monthTxns.length >= 3 && catSorted.length > 0 && (
              <Card style={{ marginBottom:16 }}>
                <div style={{ fontSize:11, fontWeight:700, color:C.text3, letterSpacing:"0.08em", textTransform:"uppercase", marginBottom:14 }}>
                  Where it went
                </div>
                <DonutChart transactions={monthTxns} month={month} />
              </Card>
            )}

            {/* Selected day panel */}
            {selDay && (
              <Card style={{ marginBottom:12, borderColor:"rgba(167,139,250,0.2)" }}>
                {/* Day header */}
                <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:10 }}>
                  <div style={{ color:C.text, fontWeight:700, fontSize:14 }}>
                    {new Date(month+"-"+String(selDay).padStart(2,"0")+"T12:00").toLocaleDateString("en",{weekday:"long",month:"long",day:"numeric"})}
                  </div>
                  <div style={{ display:"flex", gap:6 }}>
                    <button onClick={()=>setShowAdd(p=>!p)} style={{
                      background: showAdd ? "rgba(61,214,140,0.15)" : C.surface3,
                      border: `1px solid ${showAdd ? "rgba(61,214,140,0.4)" : C.border2}`,
                      color: showAdd ? C.green : C.text2,
                      borderRadius:8, padding:"5px 12px", fontSize:11, fontWeight:700, cursor:"pointer"
                    }}>
                      {showAdd ? "✕ Close" : "➕ Add"}
                    </button>
                    <button onClick={()=>{setSelDay(null);setShowAdd(false);}} style={{ background:"none", border:"none", color:C.text3, cursor:"pointer", fontSize:16 }}>✕</button>
                  </div>
                </div>

                {/* Collapsible add form */}
                {showAdd && (
                  <div style={{ marginBottom:14 }}>
                    <QuickAdd onAdd={t=>{addTx(t);}} creditCards={creditCards} defaultDate={selectedDateStr} />
                  </div>
                )}

                {/* Day transactions */}
                {dayTxns.length > 0 && (
                  <div>
                    {dayTxns.sort((a,b)=>b.id-a.id).map((txn, i) => {
                      const c = txn.expenseCategory ? getCat(txn.expenseCategory) : null;
                      const card = txn.cardId ? debtState.debts.find(d=>d.id===txn.cardId) : null;
                      return (
                        <div key={txn.id} style={{ display:"flex", alignItems:"center", gap:12, padding:"10px 0",
                          borderBottom: i < dayTxns.length - 1 ? `1px solid ${C.border}` : "none" }}>
                          <div style={{ fontSize:22, flexShrink:0 }}>{txn.icon}</div>
                          <div style={{ flex:1, minWidth:0 }}>
                            <div style={{ color:C.text, fontSize:14, fontWeight:600, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{txn.description}</div>
                            <div style={{ display:"flex", gap:6, marginTop:3, alignItems:"center", flexWrap:"wrap" }}>
                              {c && <Tag color={c.color}>{c.id}</Tag>}
                              {card && <Tag color={C.red}>💳 {card.name}</Tag>}
                              {txn.recurring && <Tag color={C.amber}>🔁</Tag>}
                              {txn.isInterestIncome && <Tag color={C.amber}>⚠️ Interest</Tag>}
                            </div>
                          </div>
                          <div style={{ color:txn.amount>0?C.green:C.red, fontWeight:700, fontSize:15, flexShrink:0 }}>
                            {txn.amount>0 ? "+" : "-"}{fmt(Math.abs(txn.amount))}
                          </div>
                          <button onClick={()=>delTx(txn.id)}
                            style={{ background:"none", border:"none", color:C.text3, cursor:"pointer", padding:"4px", display:"flex", alignItems:"center" }}
                            onMouseOver={e=>e.currentTarget.style.color=C.red}
                            onMouseOut={e=>e.currentTarget.style.color=C.text3}>
                            <Trash2 size={14} />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
                {dayTxns.length===0 && !showAdd && (
                  <div style={{ color:C.text3, fontSize:12, textAlign:"center", padding:"8px 0" }}>
                    No transactions — tap <strong style={{color:C.green}}>➕ Add</strong> to log one
                  </div>
                )}
              </Card>
            )}

            {/* Prompt when no day selected */}
            {!selDay && (
              <div style={{ textAlign:"center", padding:"16px 0 8px" }}>
                <CalendarDays size={28} color={C.text3} style={{ marginBottom:8 }} />
                <div style={{ color:C.text2, fontSize:13, fontWeight:600, marginBottom:4 }}>Tap a day to view transactions</div>
                <div style={{ color:C.text3, fontSize:11 }}>
                  <span style={{ color:C.green }}>●</span> income &nbsp;
                  <span style={{ color:C.red }}>●</span> expense &nbsp;
                  <span style={{ color:"rgba(240,82,82,0.5)" }}>■</span> high spend
                </div>
              </div>
            )}

            {/* ── ALL MONTH TRANSACTIONS LIST ── */}
            {!selDay && (
              <Card>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14 }}>
                  <Label style={{ marginBottom:0 }}>Transactions</Label>
                  {monthTxns.length > 8 && (
                    <button onClick={()=>setShowAllTxns(p=>!p)}
                      style={{ background:"none", border:"none", color:C.green, fontSize:12, fontWeight:600, cursor:"pointer", padding:0 }}>
                      {showAllTxns ? "Show less ↑" : `See all ${monthTxns.length} ↓`}
                    </button>
                  )}
                </div>

                {monthTxns.length === 0 && (
                  <div style={{ textAlign:"center", padding:"24px 0 16px" }}>
                    <div style={{ color:C.text3, fontSize:13, marginBottom:10 }}>No transactions yet. Every dirham tracked is an amanah honoured.</div>
                    <div style={{ color:C.text3, fontSize:11, marginBottom:14, lineHeight:1.6 }}>Missed a few days? No stress — add them now.</div>
                    <button onClick={() => setShowAddDrawer(true)}
                      style={{ background:"rgba(61,214,140,0.1)", border:"1px solid rgba(61,214,140,0.25)",
                        color:C.green, borderRadius:10, padding:"9px 20px", fontSize:12, fontWeight:700, cursor:"pointer" }}>
                      + Add Expense
                    </button>
                  </div>
                )}

                {(() => {
                  const fmtDate = d => new Date(d + "T12:00").toLocaleDateString("en", { weekday:"short", month:"short", day:"numeric" });

                  const sorted = [...monthTxns].sort((a,b) => b.date.localeCompare(a.date) || b.id - a.id);
                  const shown  = sorted.slice(0, showAllTxns ? undefined : 8);

                  const groups = [];
                  shown.forEach(txn => {
                    const last = groups[groups.length - 1];
                    if (last && last.date === txn.date) last.txns.push(txn);
                    else groups.push({ date: txn.date, txns: [txn] });
                  });

                  return groups.map(({ date, txns }) => (
                    <div key={date} style={{ marginBottom:12 }}>
                      <div style={{ fontSize:11, fontWeight:700, color:C.text3, letterSpacing:"0.06em",
                        textTransform:"uppercase", padding:"4px 0 6px",
                        borderBottom:`1px solid ${C.border}`, marginBottom:2 }}>
                        {fmtDate(date)}
                      </div>
                      {txns.map((txn, i) => {
                        const c = txn.expenseCategory ? getCat(txn.expenseCategory) : null;
                        return (
                          <div key={txn.id} style={{ display:"flex", alignItems:"center", gap:12,
                            padding:"10px 0",
                            borderBottom: i < txns.length - 1 ? `1px solid ${C.border}` : "none" }}>
                            <div style={{ fontSize:22, flexShrink:0 }}>{txn.icon}</div>
                            <div style={{ flex:1, minWidth:0 }}>
                              <div style={{ color:C.text, fontSize:14, fontWeight:600, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>
                                {txn.description}
                              </div>
                              <div style={{ display:"flex", gap:6, marginTop:3, alignItems:"center", flexWrap:"wrap" }}>
                                {c && <Tag color={c.color}>{c.id}</Tag>}
                                {txn.recurring && <Tag color={C.amber}>🔁</Tag>}
                                {txn.isInterestIncome && <Tag color={C.amber}>⚠️ Interest</Tag>}
                              </div>
                            </div>
                            <div style={{ color:txn.amount>0 ? C.green : C.red, fontWeight:700, fontSize:15, flexShrink:0 }}>
                              {txn.amount>0 ? "+" : "-"}{fmt(Math.abs(txn.amount))}
                            </div>
                            <button onClick={()=>delTx(txn.id)}
                              style={{ background:"none", border:"none", color:C.text3, cursor:"pointer",
                                padding:"4px", display:"flex", alignItems:"center" }}
                              onMouseOver={e=>e.currentTarget.style.color=C.red}
                              onMouseOut={e=>e.currentTarget.style.color=C.text3}>
                              <Trash2 size={14} />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  ));
                })()}
              </Card>
            )}

          </div>
        )}

        {/* ══════════════ WEALTH TAB ══════════════ */}
        {tab === "tools" && (
          <div className="fade-up">

            {/* ── Feature grid — add new tools to TOOL_TABS above, nothing here changes ── */}
            {!toolsTab && (
              <div style={{ paddingTop: 8 }}>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
                {TOOL_TABS.map((t, i) => {
                  const locked = t.pro && !proUser;
                  const isLastOdd = i === TOOL_TABS.length - 1 && TOOL_TABS.length % 2 !== 0;

                  // Live stat — replaces subtitle when the user has real data
                  let stat = null;
                  if (!t.comingSoon) {
                    if (t.id === "goals") {
                      if (goals.length > 0) {
                        const saved  = goals.reduce((s,g) => s + (g.saved  || 0), 0);
                        const target = goals.reduce((s,g) => s + (g.target || 0), 0);
                        stat = `${fmt(saved)} of ${fmt(target)}`;
                      }
                    } else if (t.id === "recurring") {
                      const active = recurring.filter(r => r.active && r.type !== "income");
                      if (active.length > 0) {
                        const monthly = active.reduce((s,r) => s + r.amount, 0);
                        stat = `${fmt(monthly)}/mo · ${active.length} bill${active.length !== 1 ? "s" : ""}`;
                      }
                    } else if (t.id === "debts") {
                      if (debtState.debts.length > 0) {
                        const total = debtState.debts.reduce((s,d) => s + (d.currentBalance || 0), 0);
                        stat = `${fmt(total)} outstanding`;
                      }
                    } else if (t.id === "zakat") {
                      const zm = zakatData?.manual || zakatData || {};
                      const manualZ = ["cash","gold","receivables","business"].reduce((s,k) => s + (parseFloat(zm[k]) || 0), 0);
                      const autoSavZ = Math.max(0, appNetSavings);
                      const autoLiabZ = debtState.debts.filter(d => getDebt(d.type).haram).reduce((s,d) => s + (d.currentBalance || 0), 0);
                      const netWorthZ = manualZ + autoSavZ - autoLiabZ;
                      const zakatDueZ = netWorthZ >= 565 ? netWorthZ * 0.025 : 0;
                      if (zakatDueZ > 0) stat = `${fmt(zakatDueZ)} due this year`;
                      else if (manualZ > 0 || autoSavZ > 0) stat = "Below nisab threshold";
                    } else if (t.id === "barakah") {
                      if (barakahEnabled) stat = "Active";
                    }
                  }

                  return (
                    <button key={t.id}
                      onClick={() => !t.comingSoon && setToolsTab(t.id)}
                      style={{
                        gridColumn: isLastOdd ? "1 / -1" : undefined,
                        background: t.gradient,
                        border: "none",
                        borderRadius:16, padding:"16px 14px",
                        cursor: t.comingSoon ? "default" : "pointer",
                        textAlign:"left", display:"flex", flexDirection:"column", gap:10,
                        animation: `fadeUp 0.35s ease ${i * 60}ms both`,
                        opacity: t.comingSoon ? 0.55 : 1,
                      }}
                      onMouseDown={e => { if (!t.comingSoon) e.currentTarget.style.opacity = "0.7"; }}
                      onMouseUp={e =>   { if (!t.comingSoon) e.currentTarget.style.opacity = "1"; }}
                      onMouseLeave={e =>{ if (!t.comingSoon) e.currentTarget.style.opacity = "1"; }}
                      onTouchStart={e =>{ if (!t.comingSoon) e.currentTarget.style.opacity = "0.7"; }}
                      onTouchEnd={e =>  { if (!t.comingSoon) e.currentTarget.style.opacity = "1"; }}
                    >
                      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
                        <div style={{
                          width:40, height:40, borderRadius:12,
                          background: t.iconBg,
                          display:"flex", alignItems:"center", justifyContent:"center", fontSize:20,
                        }}>
                          {t.emoji}
                        </div>
                        {t.comingSoon
                          ? <span style={{ fontSize:9, fontWeight:700, color:C.blue,
                              background:"rgba(96,165,250,0.15)",
                              borderRadius:20, padding:"2px 7px", letterSpacing:"0.05em" }}>SOON</span>
                          : locked
                            ? <span style={{ fontSize:9, fontWeight:700, color:C.purple,
                                background:"rgba(167,139,250,0.18)",
                                borderRadius:20, padding:"2px 7px", letterSpacing:"0.05em" }}>PRO</span>
                            : <span style={{ color:C.text3, fontSize:16, lineHeight:1 }}>→</span>
                        }
                      </div>
                      <div>
                        <div style={{ color:C.text, fontWeight:700, fontSize:14, marginBottom:3 }}>{t.label}</div>
                        <div style={{ color: stat ? C.text2 : C.text3, fontSize:11, lineHeight:1.5, fontWeight: stat ? 600 : 400 }}>
                          {stat || t.subtitle}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
              </div>
            )}

            {/* ── Feature content ── */}
            {toolsTab && (
              <div>
                <button onClick={() => setToolsTab(null)}
                  style={{ background:C.surface2, border:`1px solid ${C.border2}`,
                    color:C.text2, fontSize:13, fontWeight:600,
                    borderRadius:10, padding:"8px 14px", cursor:"pointer",
                    display:"flex", alignItems:"center", gap:6, marginBottom:14 }}>
                  ← Back
                </button>
                <Card>
                  {toolsTab==="recurring" && (
                    <RecurringTab recurring={recurring} setRecurring={setRecurring} onApplyMonth={applyRecurring} month={month} getRecurStatus={getRecurStatus} confirmRecurring={confirmRecurring} skipRecurring={skipRecurring} />
                  )}
                  {toolsTab==="goals" && (
                    proUser
                      ? <GoalsTab goals={goals} setGoals={setGoals} transactions={transactions} setTransactions={setTransactions} />
                      : <GoalsTab goals={goals} setGoals={(updated) => {
                          if (updated.length > 3) { openUpgrade("goals_unlimited"); return; }
                          setGoals(updated);
                        }} transactions={transactions} setTransactions={setTransactions} freeLimit={3} openUpgrade={openUpgrade} />
                  )}
                  {toolsTab==="debts" && (
                    proUser
                      ? <DebtTab debtState={debtState} setDebtState={setDebtState} transactions={monthTxns} proUser={proUser} openUpgrade={openUpgrade} />
                      : <DebtTab debtState={debtState} setDebtState={setDebtState} transactions={monthTxns} proUser={proUser} openUpgrade={openUpgrade} freeLimit={2} />
                  )}
                  {toolsTab==="zakat" && <ZakatTab zakatData={zakatData} setZakatData={setZakatData} transactions={transactions} debtState={debtState} netSavings={appNetSavings} proUser={proUser} openUpgrade={openUpgrade} addTx={addTx} settings={settings} setSettings={setSettings} />}
                  {toolsTab==="barakah" && <BarakahHabitsTab enabled={barakahEnabled} onEnable={enableBarakah} />}
                </Card>
              </div>
            )}

          </div>
        )}

        {/* ══════════════ SETTINGS TAB ══════════════ */}
        {tab === "settings" && (
          <SettingsTab
            settings={settings} setSettings={setSettings}
            budgetData={budgetData} setBudgetData={setBudgetData}
            proUser={proUser} openUpgrade={openUpgrade}
            transactions={transactions} debtState={debtState} recurring={recurring}
            goals={goals} zakatData={zakatData} netWorthData={netWorthData}
            onImport={handleImport} doExport={doExport}
          />
        )}

        <div style={{ textAlign:"center", marginTop:16 }}>
          <p style={{ color:C.text3, fontSize:11, fontStyle:"italic" }}>📚 Not financial advice. For rulings, consult a qualified Islamic scholar.</p>
        </div>
      </div>

      {/* AI chat — opened from header button */}

      {/* ── ADD TRANSACTION DRAWER (slide up) ── */}
      {showAddDrawer && (
        <>
          {/* Backdrop */}
          <div onClick={()=>setShowAddDrawer(false)}
            style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.6)", zIndex:400 }} />
          {/* Drawer — zIndex 401 so it sits above the bottom nav (300) */}
          <div style={{ position:"fixed", bottom:0, left:0, right:0, background:C.surface, borderRadius:"24px 24px 0 0",
            padding:"0 20px 0", zIndex:401, maxWidth:560, margin:"0 auto",
            boxShadow:"0 -12px 50px rgba(0,0,0,0.6)", animation:"fadeUp 0.25s ease",
            maxHeight:"90vh", overflowY:"auto", WebkitOverflowScrolling:"touch",
            paddingBottom:"env(safe-area-inset-bottom, 16px)" }}>
            {/* Drag handle */}
            <div style={{ display:"flex", justifyContent:"center", padding:"10px 0 4px" }}>
              <div style={{ width:36, height:4, borderRadius:2, background:"rgba(255,255,255,0.15)" }} />
            </div>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:18 }}>
              <div style={{ color:C.text, fontWeight:700, fontSize:18 }}>Add Transaction</div>
              <button onClick={()=>setShowAddDrawer(false)}
                style={{ background:C.surface2, border:`1px solid ${C.border}`, color:C.text3, borderRadius:50, width:30, height:30, fontSize:16, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center" }}>×</button>
            </div>
            <QuickAdd onAdd={t=>{ addTx(t); setShowAddDrawer(false); }} creditCards={creditCards} isFirst={transactions.length === 0} />
            {/* Bottom spacer — clears the mobile nav */}
            <div style={{ height: isMobile ? 100 : 24 }} />
          </div>
        </>
      )}

      {/* AI CHAT — hidden until native app feedback confirms demand */}
      {/* {showChat && <AiChat transactions={transactions} debts={debtState.debts} goals={goals} onClose={()=>setShowChat(false)} />} */}

      {/* Pro payment toast */}
      {proToast && (
        <div style={{ position:"fixed", top: isMobile ? "auto" : 20, bottom: isMobile ? 90 : "auto",
          left:"50%", transform:"translateX(-50%)", zIndex:600,
          background: proToast.ok ? "rgba(61,214,140,0.95)" : "rgba(240,82,82,0.95)",
          color:"#0e0e13", borderRadius:12, padding:"12px 20px",
          fontSize:13, fontWeight:700, whiteSpace:"nowrap",
          boxShadow:"0 4px 20px rgba(0,0,0,0.4)", animation:"fadeUp 0.3s ease" }}>
          {proToast.msg}
        </div>
      )}

      {/* ── MOBILE SIDE NAV ──────────────────────────────────────
           Change NAV_VARIANT to switch styles:
           1 = vertical pill, 3 icons (Settings stays in header)
           2 = vertical pill, 4 icons (Settings inside pill)
           3 = expandable circles with label slide-out
      ── */}
      {isMobile && (() => {
        const NAV_VARIANT = 2;

        const items = [
          { id:"overview", Icon:Home,        label:"Home"     },
          { id:"tools",    Icon:Sprout,       label:"Wealth"   },
          { id:"calendar", Icon:CalendarDays, label:"Calendar" },
          ...(NAV_VARIANT === 2 ? [{ id:"settings", Icon:Settings, label:"Settings" }] : []),
        ];

        const tap = (id) => { setTab(id); if (id !== "tools") setToolsTab(null); };

        // ── Variant 1 & 2: horizontal pill at bottom ──────────
        if (NAV_VARIANT === 1 || NAV_VARIANT === 2) return (
          <div style={{
            position:"fixed", bottom:`calc(env(safe-area-inset-bottom, 0px) + 2px)`,
            left:"50%", transform:"translateX(-50%)", zIndex:300,
            background:"rgba(22,22,30,0.75)", border:`1px solid ${C.border}`,
            backdropFilter:"blur(24px)", WebkitBackdropFilter:"blur(24px)",
            borderRadius:40, padding:"6px 10px",
            display:"flex", flexDirection:"row", gap:8,
          }}>
            {items.map(({ id, Icon }) => {
              const isActive = tab === id;
              return (
                <button key={id} onClick={() => tap(id)} style={{
                  width:60, height:46, borderRadius:14, border:"none", cursor:"pointer",
                  display:"flex", alignItems:"center", justifyContent:"center",
                  background: isActive ? C.purpleDim : "transparent",
                  WebkitTapHighlightColor:"transparent", transition:"background 0.2s",
                }}>
                  <Icon size={22} color={isActive ? C.purple : C.text3} strokeWidth={1.8} />
                </button>
              );
            })}
          </div>
        );

        // ── Variant 3: bottom bar, circle + instant label ─────
        return (
          <div style={{
            position:"fixed", bottom:0, left:0, right:0, zIndex:300,
            background:"rgba(22,22,30,0.75)",
            backdropFilter:"blur(24px)", WebkitBackdropFilter:"blur(24px)",
            borderTop:`1px solid ${C.border}`,
            display:"flex", alignItems:"center",
            paddingTop:8,
            paddingBottom:`calc(env(safe-area-inset-bottom, 0px) + 8px)`,
          }}>
            {items.map(({ id, Icon, label }) => {
              const isActive = tab === id;
              return (
                <button key={id} onClick={() => tap(id)} style={{
                  flex:1, display:"flex", alignItems:"center", justifyContent:"center",
                  gap:8, padding:"4px",
                  background:"none", border:"none", cursor:"pointer",
                  WebkitTapHighlightColor:"transparent",
                }}>
                  <div style={{
                    width:44, height:44, borderRadius:"50%",
                    display:"flex", alignItems:"center", justifyContent:"center",
                    background: isActive ? C.purpleDim : "transparent",
                    border:`1px solid ${isActive ? C.purpleDim : "transparent"}`,
                  }}>
                    <Icon size={22} color={isActive ? C.purple : C.text3} strokeWidth={1.8} />
                  </div>
                  {isActive && (
                    <span style={{ fontSize:13, fontWeight:700, color:C.purple, whiteSpace:"nowrap" }}>
                      {label}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        );
      })()}

      {/* Undo toast */}
      {undoToast && (
        <div style={{ position:"fixed", bottom:24, left:"50%", transform:"translateX(-50%)", zIndex:2000,
          background:C.surface, border:`1px solid ${C.border}`, borderRadius:12,
          padding:"12px 16px", display:"flex", alignItems:"center", gap:12,
          boxShadow:"0 8px 32px rgba(0,0,0,0.5)", whiteSpace:"nowrap",
          animation:"slideUp 0.25s ease" }}>
          <span style={{ color:C.text, fontSize:13 }}>{undoToast.label}</span>
          <button onClick={() => { undoToast.action(); setUndoToast(null); }}
            style={{ background:"rgba(61,214,140,0.15)", border:`1px solid ${C.green}`,
              color:C.green, borderRadius:8, padding:"4px 12px", fontSize:12,
              fontWeight:700, cursor:"pointer" }}>
            Undo
          </button>
        </div>
      )}
      {showTour && <Tour onEnd={()=>setShowTour(false)} setTab={setTab} setToolsTab={setToolsTab} />}
      {showUpgrade && <UpgradeModal onClose={() => setShowUpgrade(false)} trigger={upgradeTrigger} />}
      <ModalIfOpen />
      {showTests && <TestRunner onClose={()=>setShowTests(false)} />}
      {/* Hidden test trigger — 5 taps on bottom center */}
      <div onPointerDown={() => {
        tapCount.current++;
        clearTimeout(tapTimer.current);
        if (tapCount.current >= 5) { tapCount.current = 0; setShowTests(true); }
        tapTimer.current = setTimeout(() => { tapCount.current = 0; }, 1500);
      }} style={{ position:"fixed", bottom:0, left:"50%", transform:"translateX(-50%)", width:80, height:24, cursor:"default", zIndex:999 }} />
    </div>
  );
}
